import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Table, Button, Form,
  InputGroup, Badge, Spinner, Pagination, OverlayTrigger, Tooltip, Modal, ProgressBar, Dropdown, DropdownButton
} from 'react-bootstrap';
import { getApiUrl } from '../config/api';

// ── Mapping icon FA lama → Bootstrap Icons ──
const resolveIcon = (value) => {
  if (!value) return null;
  if (value.includes('fa-download')) return 'bi bi-cloud-download-fill';
  if (value.includes('fa-cogs'))     return 'bi bi-gear-fill';
  if (value.includes('fa-check'))    return 'bi bi-check-circle-fill';
  if (value.includes('fa-times'))    return 'bi bi-x-circle-fill';
  return 'bi bi-file-earmark-fill';
};

// ── Component DataTagihanPDF ──

const DataTagihanPDF = () => {
  const navigate = useNavigate();
  const [time, setTime] = useState({ date: '', clock: '' });

  // ── Helper format tanggal YYYY-MM-DD untuk input type date ──
  const getTodayStr = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const getFirstDayOfMonthStr = () => {
    const d = new Date();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-01`;
  };

  // ── Data State ──
  const [tagihanList, setTagihanList] = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const [fetchError, setFetchError]   = useState('');

  // ── Periode & Jenis Filter State ──
  const [periode1, setPeriode1] = useState(getFirstDayOfMonthStr());
  const [periode2, setPeriode2] = useState(getTodayStr());
  const [jenis, setJenis]       = useState('RJ'); // Default Rawat Jalan ('RJ')

  // ── Upload Modal State ──
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [excelFile, setExcelFile]             = useState(null);
  const [isUploading, setIsUploading]         = useState(false);
  const [uploadProgress, setUploadProgress]   = useState(0);
  const [uploadFeedback, setUploadFeedback]   = useState(null);

  const handleOpenUploadModal = () => {
    setExcelFile(null);
    setUploadProgress(0);
    setUploadFeedback(null);
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setExcelFile(null);
    setUploadProgress(0);
    setUploadFeedback(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setExcelFile(e.target.files[0]);
      setUploadProgress(0);
      setUploadFeedback(null);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!excelFile) {
      setUploadFeedback({ type: 'danger', text: 'Pilih file Excel terlebih dahulu!' });
      return;
    }

    setIsUploading(true);
    setUploadProgress(15);
    setUploadFeedback(null);
    const apiUrl = getApiUrl();

    const formData = new FormData();
    formData.append('file', excelFile);

    const progressInterval = setInterval(() => {
      setUploadProgress(prev => (prev < 85 ? prev + 15 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/prosesimport`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const json = await res.json();
      setUploadFeedback({
        type: 'success',
        text: json?.message || 'Data NCC BPJS berhasil diimport!'
      });
      setTimeout(() => {
        handleCloseUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);
    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal upload ke /prosesimport:', err);
      setUploadProgress(100);
      setUploadFeedback({
        type: 'warning',
        text: 'Menggunakan mode demo. Gagal terhubung ke backend /prosesimport.'
      });
    } finally {
      setIsUploading(false);
    }
  };

  // ── Upload SEP Modal State (iprosessep) ──
  const [showSepUploadModal, setShowSepUploadModal] = useState(false);
  const [sepFile, setSepFile]                       = useState(null);
  const [selectedSepItem, setSelectedSepItem]       = useState(null);
  const [isUploadingSep, setIsUploadingSep]         = useState(false);
  const [uploadProgressSep, setUploadProgressSep]   = useState(0);
  const [uploadFeedbackSep, setUploadFeedbackSep]   = useState(null);

  const handleOpenSepUploadModal = (item = null) => {
    setSelectedSepItem(item);
    setSepFile(null);
    setUploadProgressSep(0);
    setUploadFeedbackSep(null);
    setShowSepUploadModal(true);
  };

  const handleCloseSepUploadModal = () => {
    setShowSepUploadModal(false);
    setSelectedSepItem(null);
    setSepFile(null);
    setUploadProgressSep(0);
    setUploadFeedbackSep(null);
  };

  // ── Upload Tagihan Modal State (iprosestag) ──
  const [showTagUploadModal, setShowTagUploadModal] = useState(false);
  const [tagFile, setTagFile]                       = useState(null);
  const [selectedTagItem, setSelectedTagItem]       = useState(null);
  const [isUploadingTag, setIsUploadingTag]         = useState(false);
  const [uploadProgressTag, setUploadProgressTag]   = useState(0);
  const [uploadFeedbackTag, setUploadFeedbackTag]   = useState(null);

  const handleOpenTagUploadModal = (item = null) => {
    setSelectedTagItem(item);
    setTagFile(null);
    setUploadProgressTag(0);
    setUploadFeedbackTag(null);
    setShowTagUploadModal(true);
  };

  const handleCloseTagUploadModal = () => {
    setShowTagUploadModal(false);
    setSelectedTagItem(null);
    setTagFile(null);
    setUploadProgressTag(0);
    setUploadFeedbackTag(null);
  };

  // ── Upload INA-CBGs Modal State (iprosesina) ──
  const [showInaUploadModal, setShowInaUploadModal] = useState(false);
  const [inaFile, setInaFile]                       = useState(null);
  const [selectedInaItem, setSelectedInaItem]       = useState(null);
  const [isUploadingIna, setIsUploadingIna]         = useState(false);
  const [uploadProgressIna, setUploadProgressIna]   = useState(0);
  const [uploadFeedbackIna, setUploadFeedbackIna]   = useState(null);

  const handleOpenInaUploadModal = (item = null) => {
    setSelectedInaItem(item);
    setInaFile(null);
    setUploadProgressIna(0);
    setUploadFeedbackIna(null);
    setShowInaUploadModal(true);
  };

  const handleCloseInaUploadModal = () => {
    setShowInaUploadModal(false);
    setSelectedInaItem(null);
    setInaFile(null);
    setUploadProgressIna(0);
    setUploadFeedbackIna(null);
  };

  // ── Upload Lab Modal State (iproseslab) ──
  const [showLabUploadModal, setShowLabUploadModal] = useState(false);
  const [labFile, setLabFile]                       = useState(null);
  const [selectedLabItem, setSelectedLabItem]       = useState(null);
  const [isUploadingLab, setIsUploadingLab]         = useState(false);
  const [uploadProgressLab, setUploadProgressLab]   = useState(0);
  const [uploadFeedbackLab, setUploadFeedbackLab]   = useState(null);

  const handleOpenLabUploadModal = (item = null) => {
    setSelectedLabItem(item);
    setLabFile(null);
    setUploadProgressLab(0);
    setUploadFeedbackLab(null);
    setShowLabUploadModal(true);
  };

  const handleCloseLabUploadModal = () => {
    setShowLabUploadModal(false);
    setSelectedLabItem(null);
    setLabFile(null);
    setUploadProgressLab(0);
    setUploadFeedbackLab(null);
  };

  // ── Upload Rad Modal State (iprosesrad) ──
  const [showRadUploadModal, setShowRadUploadModal] = useState(false);
  const [radFile, setRadFile]                       = useState(null);
  const [selectedRadItem, setSelectedRadItem]       = useState(null);
  const [isUploadingRad, setIsUploadingRad]         = useState(false);
  const [uploadProgressRad, setUploadProgressRad]   = useState(0);
  const [uploadFeedbackRad, setUploadFeedbackRad]   = useState(null);

  const handleOpenRadUploadModal = (item = null) => {
    setSelectedRadItem(item);
    setRadFile(null);
    setUploadProgressRad(0);
    setUploadFeedbackRad(null);
    setShowRadUploadModal(true);
  };

  const handleCloseRadUploadModal = () => {
    setShowRadUploadModal(false);
    setSelectedRadItem(null);
    setRadFile(null);
    setUploadProgressRad(0);
    setUploadFeedbackRad(null);
  };

  // ── Upload Dokumen Lain Modal State (iproseslain) ──
  const [showLainUploadModal, setShowLainUploadModal] = useState(false);
  const [lainFile, setLainFile]                       = useState(null);
  const [selectedLainItem, setSelectedLainItem]       = useState(null);
  const [isUploadingLain, setIsUploadingLain]         = useState(false);
  const [uploadProgressLain, setUploadProgressLain]   = useState(0);
  const [uploadFeedbackLain, setUploadFeedbackLain]   = useState(null);

  const handleOpenLainUploadModal = (item = null) => {
    setSelectedLainItem(item);
    setLainFile(null);
    setUploadProgressLain(0);
    setUploadFeedbackLain(null);
    setShowLainUploadModal(true);
  };

  const handleCloseLainUploadModal = () => {
    setShowLainUploadModal(false);
    setSelectedLainItem(null);
    setLainFile(null);
    setUploadProgressLain(0);
    setUploadFeedbackLain(null);
  };

  // ── Gabung Confirmation Modal State ──
  const [showGabungConfirm, setShowGabungConfirm] = useState(false);
  const [gabungConfirmItem, setGabungConfirmItem] = useState(null);

  const handleOpenGabungConfirm = (item) => {
    setGabungConfirmItem(item);
    setShowGabungConfirm(true);
  };

  const handleCloseGabungConfirm = () => {
    setShowGabungConfirm(false);
    setGabungConfirmItem(null);
  };

  const handleConfirmGabung = () => {
    if (gabungConfirmItem) {
      handleProsesDokumen('Gabung', gabungConfirmItem);
    }
    handleCloseGabungConfirm();
  };

  // ── Upload ALL SEP (Batch Upload) State ──
  const [showBatchTagihanModal, setShowBatchTagihanModal] = useState(false);
  const [batchTagihanFiles, setBatchTagihanFiles]         = useState([]);
  const [isBatchUploadingTagihan, setIsBatchUploadingTagihan] = useState(false);
  const [batchProgressTagihan, setBatchProgressTagihan]       = useState(0);
  const [batchStatusTextTagihan, setBatchStatusTextTagihan]   = useState('');
  const [batchLogTagihan, setBatchLogTagihan]                 = useState([]);

  const handleOpenBatchTagihanModal = () => {
    setBatchTagihanFiles([]);
    setIsBatchUploadingTagihan(false);
    setBatchProgressTagihan(0);
    setBatchStatusTextTagihan('');
    setBatchLogTagihan([]);
    setShowBatchTagihanModal(true);
  };

  const handleCloseBatchTagihanModal = () => {
    if (isBatchUploadingTagihan) return;
    setShowBatchTagihanModal(false);
    setBatchTagihanFiles([]);
    setBatchLogTagihan([]);
  };

  const handleBatchTagihanFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf');
      setBatchTagihanFiles(selected);
      setBatchProgressTagihan(0);
      setBatchLogTagihan([]);
      setBatchStatusTextTagihan(`Terpilih ${selected.length} file PDF Tagihan.`);
    }
  };

  const handleBatchTagihanUploadSubmit = async (e) => {
    e.preventDefault();
    if (batchTagihanFiles.length === 0) {
      alert('Silakan pilih minimal 1 file PDF Tagihan!');
      return;
    }

    setIsBatchUploadingTagihan(true);
    setBatchProgressTagihan(15);
    setBatchStatusTextTagihan(`Mengirimkan ${batchTagihanFiles.length} file PDF Tagihan ke backend /uploaddatatagall...`);
    setBatchLogTagihan([]);
    const apiUrl = getApiUrl();

    const formData = new FormData();
    batchTagihanFiles.forEach(file => {
      formData.append('file[]', file);
      formData.append('files', file);
      formData.append('file', file);
    });

    const progressInterval = setInterval(() => {
      setBatchProgressTagihan(prev => (prev < 88 ? prev + 12 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatatagall`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setBatchProgressTagihan(100);

      let json = {};
      try {
        json = await res.json();
      } catch (err) {}

      if (!res.ok) {
        setBatchStatusTextTagihan(`Gagal upload batch: Response HTTP ${res.status}`);
        setBatchLogTagihan(batchTagihanFiles.map(f => ({
          fileName: f.name,
          no_sep: f.name.replace(/\.[^/.]+$/, ""),
          status: 'danger',
          msg: json?.msg || json?.message || `HTTP ${res.status}`
        })));
        return;
      }

      setBatchStatusTextTagihan(json?.msg || json?.message || `Berhasil mengunggah ${batchTagihanFiles.length} file Tagihan sekaligus!`);
      setBatchLogTagihan(batchTagihanFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: json?.msg || json?.message || 'Berhasil diimport!'
      })));

      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal fetch ke /uploaddatatagall:', err);
      setBatchProgressTagihan(100);
      setBatchStatusTextTagihan(`Batch upload ${batchTagihanFiles.length} file Tagihan selesai (Demo Mode).`);
      setBatchLogTagihan(batchTagihanFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: `[Demo Mode] Terunggah ke /uploaddatatagall`
      })));
      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);
    } finally {
      setIsBatchUploadingTagihan(false);
    }
  };

  const [showBatchInacbgsModal, setShowBatchInacbgsModal] = useState(false);
  const [batchInacbgsFiles, setBatchInacbgsFiles]         = useState([]);
  const [isBatchUploadingInacbgs, setIsBatchUploadingInacbgs] = useState(false);
  const [batchProgressInacbgs, setBatchProgressInacbgs]       = useState(0);
  const [batchStatusTextInacbgs, setBatchStatusTextInacbgs]   = useState('');
  const [batchLogInacbgs, setBatchLogInacbgs]                 = useState([]);

  const handleOpenBatchInacbgsModal = () => {
    setBatchInacbgsFiles([]);
    setIsBatchUploadingInacbgs(false);
    setBatchProgressInacbgs(0);
    setBatchStatusTextInacbgs('');
    setBatchLogInacbgs([]);
    setShowBatchInacbgsModal(true);
  };

  const handleCloseBatchInacbgsModal = () => {
    if (isBatchUploadingInacbgs) return;
    setShowBatchInacbgsModal(false);
    setBatchInacbgsFiles([]);
    setBatchLogInacbgs([]);
  };

  const handleBatchInacbgsFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf');
      setBatchInacbgsFiles(selected);
      setBatchProgressInacbgs(0);
      setBatchLogInacbgs([]);
      setBatchStatusTextInacbgs(`Terpilih ${selected.length} file PDF INA-CBGS.`);
    }
  };

  const handleBatchInacbgsUploadSubmit = async (e) => {
    e.preventDefault();
    if (batchInacbgsFiles.length === 0) {
      alert('Silakan pilih minimal 1 file PDF INA-CBGS!');
      return;
    }

    setIsBatchUploadingInacbgs(true);
    setBatchProgressInacbgs(15);
    setBatchStatusTextInacbgs(`Mengirimkan ${batchInacbgsFiles.length} file PDF INA-CBGS ke backend /uploaddatainaall...`);
    setBatchLogInacbgs([]);
    const apiUrl = getApiUrl();

    const formData = new FormData();
    batchInacbgsFiles.forEach(file => {
      formData.append('file[]', file);
      formData.append('files', file);
      formData.append('file', file);
    });

    const progressInterval = setInterval(() => {
      setBatchProgressInacbgs(prev => (prev < 88 ? prev + 12 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatainaall`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setBatchProgressInacbgs(100);

      let json = {};
      try {
        json = await res.json();
      } catch (err) {}

      if (!res.ok) {
        setBatchStatusTextInacbgs(`Gagal upload batch: Response HTTP ${res.status}`);
        setBatchLogInacbgs(batchInacbgsFiles.map(f => ({
          fileName: f.name,
          no_sep: f.name.replace(/\.[^/.]+$/, ""),
          status: 'danger',
          msg: json?.msg || json?.message || `HTTP ${res.status}`
        })));
        return;
      }

      setBatchStatusTextInacbgs(json?.msg || json?.message || `Berhasil mengunggah ${batchInacbgsFiles.length} file INA-CBGS sekaligus!`);
      setBatchLogInacbgs(batchInacbgsFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: json?.msg || json?.message || 'Berhasil diimport!'
      })));

      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal fetch ke /uploaddatainaall:', err);
      setBatchProgressInacbgs(100);
      setBatchStatusTextInacbgs(`Batch upload ${batchInacbgsFiles.length} file INA-CBGS selesai (Demo Mode).`);
      setBatchLogInacbgs(batchInacbgsFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: `[Demo Mode] Terunggah ke /uploaddatainaall`
      })));
      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);
    } finally {
      setIsBatchUploadingInacbgs(false);
    }
  };

  const [showBatchLabModal, setShowBatchLabModal] = useState(false);
  const [batchLabFiles, setBatchLabFiles]         = useState([]);
  const [isBatchUploadingLab, setIsBatchUploadingLab] = useState(false);
  const [batchProgressLab, setBatchProgressLab]       = useState(0);
  const [batchStatusTextLab, setBatchStatusTextLab]   = useState('');
  const [batchLogLab, setBatchLogLab]                 = useState([]);

  const handleOpenBatchLabModal = () => {
    setBatchLabFiles([]);
    setIsBatchUploadingLab(false);
    setBatchProgressLab(0);
    setBatchStatusTextLab('');
    setBatchLogLab([]);
    setShowBatchLabModal(true);
  };

  const handleCloseBatchLabModal = () => {
    if (isBatchUploadingLab) return;
    setShowBatchLabModal(false);
    setBatchLabFiles([]);
    setBatchLogLab([]);
  };

  const handleBatchLabFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf');
      setBatchLabFiles(selected);
      setBatchProgressLab(0);
      setBatchLogLab([]);
      setBatchStatusTextLab(`Terpilih ${selected.length} file PDF Laboratorium.`);
    }
  };

  const handleBatchLabUploadSubmit = async (e) => {
    e.preventDefault();
    if (batchLabFiles.length === 0) {
      alert('Silakan pilih minimal 1 file PDF Laboratorium!');
      return;
    }

    setIsBatchUploadingLab(true);
    setBatchProgressLab(15);
    setBatchStatusTextLab(`Mengirimkan ${batchLabFiles.length} file PDF Laboratorium ke backend /uploaddatalaball...`);
    setBatchLogLab([]);
    const apiUrl = getApiUrl();

    const formData = new FormData();
    batchLabFiles.forEach(file => {
      formData.append('file[]', file);
      formData.append('files', file);
      formData.append('file', file);
    });

    const progressInterval = setInterval(() => {
      setBatchProgressLab(prev => (prev < 88 ? prev + 12 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatalaball`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setBatchProgressLab(100);

      let json = {};
      try {
        json = await res.json();
      } catch (err) {}

      if (!res.ok) {
        setBatchStatusTextLab(`Gagal upload batch: Response HTTP ${res.status}`);
        setBatchLogLab(batchLabFiles.map(f => ({
          fileName: f.name,
          no_sep: f.name.replace(/\.[^/.]+$/, ""),
          status: 'danger',
          msg: json?.msg || json?.message || `HTTP ${res.status}`
        })));
        return;
      }

      setBatchStatusTextLab(json?.msg || json?.message || `Berhasil mengunggah ${batchLabFiles.length} file Laboratorium sekaligus!`);
      setBatchLogLab(batchLabFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: json?.msg || json?.message || 'Berhasil diimport!'
      })));

      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal fetch ke /uploaddatalaball:', err);
      setBatchProgressLab(100);
      setBatchStatusTextLab(`Batch upload ${batchLabFiles.length} file Laboratorium selesai (Demo Mode).`);
      setBatchLogLab(batchLabFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: `[Demo Mode] Terunggah ke /uploaddatalaball`
      })));
      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);
    } finally {
      setIsBatchUploadingLab(false);
    }
  };

  const [showBatchRadModal, setShowBatchRadModal] = useState(false);
  const [batchRadFiles, setBatchRadFiles]         = useState([]);
  const [isBatchUploadingRad, setIsBatchUploadingRad] = useState(false);
  const [batchProgressRad, setBatchProgressRad]       = useState(0);
  const [batchStatusTextRad, setBatchStatusTextRad]   = useState('');
  const [batchLogRad, setBatchLogRad]                 = useState([]);

  const handleOpenBatchRadModal = () => {
    setBatchRadFiles([]);
    setIsBatchUploadingRad(false);
    setBatchProgressRad(0);
    setBatchStatusTextRad('');
    setBatchLogRad([]);
    setShowBatchRadModal(true);
  };

  const handleCloseBatchRadModal = () => {
    if (isBatchUploadingRad) return;
    setShowBatchRadModal(false);
    setBatchRadFiles([]);
    setBatchLogRad([]);
  };

  const handleBatchRadFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf');
      setBatchRadFiles(selected);
      setBatchProgressRad(0);
      setBatchLogRad([]);
      setBatchStatusTextRad(`Terpilih ${selected.length} file PDF Radiologi.`);
    }
  };

  const handleBatchRadUploadSubmit = async (e) => {
    e.preventDefault();
    if (batchRadFiles.length === 0) {
      alert('Silakan pilih minimal 1 file PDF Radiologi!');
      return;
    }

    setIsBatchUploadingRad(true);
    setBatchProgressRad(15);
    setBatchStatusTextRad(`Mengirimkan ${batchRadFiles.length} file PDF Radiologi ke backend /uploaddataradall...`);
    setBatchLogRad([]);
    const apiUrl = getApiUrl();

    const formData = new FormData();
    batchRadFiles.forEach(file => {
      formData.append('file[]', file);
      formData.append('files', file);
      formData.append('file', file);
    });

    const progressInterval = setInterval(() => {
      setBatchProgressRad(prev => (prev < 88 ? prev + 12 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddataradall`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setBatchProgressRad(100);

      let json = {};
      try {
        json = await res.json();
      } catch (err) {}

      if (!res.ok) {
        setBatchStatusTextRad(`Gagal upload batch: Response HTTP ${res.status}`);
        setBatchLogRad(batchRadFiles.map(f => ({
          fileName: f.name,
          no_sep: f.name.replace(/\.[^/.]+$/, ""),
          status: 'danger',
          msg: json?.msg || json?.message || `HTTP ${res.status}`
        })));
        return;
      }

      setBatchStatusTextRad(json?.msg || json?.message || `Berhasil mengunggah ${batchRadFiles.length} file Radiologi sekaligus!`);
      setBatchLogRad(batchRadFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: json?.msg || json?.message || 'Berhasil diimport!'
      })));

      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal fetch ke /uploaddataradall:', err);
      setBatchProgressRad(100);
      setBatchStatusTextRad(`Batch upload ${batchRadFiles.length} file Radiologi selesai (Demo Mode).`);
      setBatchLogRad(batchRadFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: `[Demo Mode] Terunggah ke /uploaddataradall`
      })));
      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);
    } finally {
      setIsBatchUploadingRad(false);
    }
  };

  const [showBatchLainModal, setShowBatchLainModal] = useState(false);
  const [batchLainFiles, setBatchLainFiles]         = useState([]);
  const [isBatchUploadingLain, setIsBatchUploadingLain] = useState(false);
  const [batchProgressLain, setBatchProgressLain]       = useState(0);
  const [batchStatusTextLain, setBatchStatusTextLain]   = useState('');
  const [batchLogLain, setBatchLogLain]                 = useState([]);

  const handleOpenBatchLainModal = () => {
    setBatchLainFiles([]);
    setIsBatchUploadingLain(false);
    setBatchProgressLain(0);
    setBatchStatusTextLain('');
    setBatchLogLain([]);
    setShowBatchLainModal(true);
  };

  const handleCloseBatchLainModal = () => {
    if (isBatchUploadingLain) return;
    setShowBatchLainModal(false);
    setBatchLainFiles([]);
    setBatchLogLain([]);
  };

  const handleBatchLainFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf');
      setBatchLainFiles(selected);
      setBatchProgressLain(0);
      setBatchLogLain([]);
      setBatchStatusTextLain(`Terpilih ${selected.length} file PDF Lain-lain.`);
    }
  };

  const handleBatchLainUploadSubmit = async (e) => {
    e.preventDefault();
    if (batchLainFiles.length === 0) {
      alert('Silakan pilih minimal 1 file PDF Lain-lain!');
      return;
    }

    setIsBatchUploadingLain(true);
    setBatchProgressLain(15);
    setBatchStatusTextLain(`Mengirimkan ${batchLainFiles.length} file PDF Lain-lain ke backend /uploaddatalainall...`);
    setBatchLogLain([]);
    const apiUrl = getApiUrl();

    const formData = new FormData();
    batchLainFiles.forEach(file => {
      formData.append('file[]', file);
      formData.append('files', file);
      formData.append('file', file);
    });

    const progressInterval = setInterval(() => {
      setBatchProgressLain(prev => (prev < 88 ? prev + 12 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatalainall`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setBatchProgressLain(100);

      let json = {};
      try {
        json = await res.json();
      } catch (err) {}

      if (!res.ok) {
        setBatchStatusTextLain(`Gagal upload batch: Response HTTP ${res.status}`);
        setBatchLogLain(batchLainFiles.map(f => ({
          fileName: f.name,
          no_sep: f.name.replace(/\.[^/.]+$/, ""),
          status: 'danger',
          msg: json?.msg || json?.message || `HTTP ${res.status}`
        })));
        return;
      }

      setBatchStatusTextLain(json?.msg || json?.message || `Berhasil mengunggah ${batchLainFiles.length} file Lain-lain sekaligus!`);
      setBatchLogLain(batchLainFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: json?.msg || json?.message || 'Berhasil diimport!'
      })));

      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal fetch ke /uploaddatalainall:', err);
      setBatchProgressLain(100);
      setBatchStatusTextLain(`Batch upload ${batchLainFiles.length} file Lain-lain selesai (Demo Mode).`);
      setBatchLogLain(batchLainFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: `[Demo Mode] Terunggah ke /uploaddatalainall`
      })));
      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);
    } finally {
      setIsBatchUploadingLain(false);
    }
  };

  const [showBatchSepModal, setShowBatchSepModal] = useState(false);
  const [batchSepFiles, setBatchSepFiles]         = useState([]);
  const [isBatchUploading, setIsBatchUploading]   = useState(false);
  const [batchProgress, setBatchProgress]         = useState(0);
  const [batchStatusText, setBatchStatusText]     = useState('');
  const [batchLog, setBatchLog]                   = useState([]);

  const handleOpenBatchSepModal = () => {
    setBatchSepFiles([]);
    setIsBatchUploading(false);
    setBatchProgress(0);
    setBatchStatusText('');
    setBatchLog([]);
    setShowBatchSepModal(true);
  };

  const handleCloseBatchSepModal = () => {
    if (isBatchUploading) return;
    setShowBatchSepModal(false);
    setBatchSepFiles([]);
    setBatchLog([]);
  };

  const handleBatchSepFilesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf');
      setBatchSepFiles(selected);
      setBatchProgress(0);
      setBatchLog([]);
      setBatchStatusText(`Terpilih ${selected.length} file PDF SEP.`);
    }
  };

  const handleBatchSepUploadSubmit = async (e) => {
    e.preventDefault();
    if (batchSepFiles.length === 0) {
      alert('Silakan pilih minimal 1 file PDF SEP!');
      return;
    }

    setIsBatchUploading(true);
    setBatchProgress(15);
    setBatchStatusText(`Mengirimkan ${batchSepFiles.length} file PDF SEP ke backend /uploaddatasepall...`);
    setBatchLog([]);
    const apiUrl = getApiUrl();

    // Multipart FormData berisi seluruh file PDF untuk /uploaddatasepall
    const formData = new FormData();
    batchSepFiles.forEach(file => {
      formData.append('file[]', file);
      formData.append('files', file);
      formData.append('file', file);
    });

    const progressInterval = setInterval(() => {
      setBatchProgress(prev => (prev < 88 ? prev + 12 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatasepall`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setBatchProgress(100);

      let json = {};
      try {
        json = await res.json();
      } catch (err) {}

      if (!res.ok) {
        setBatchStatusText(`Gagal upload batch: Response HTTP ${res.status}`);
        setBatchLog(batchSepFiles.map(f => ({
          fileName: f.name,
          no_sep: f.name.replace(/\.[^/.]+$/, ""),
          status: 'danger',
          msg: json?.msg || json?.message || `HTTP ${res.status}`
        })));
        return;
      }

      setBatchStatusText(json?.msg || json?.message || `Berhasil mengunggah ${batchSepFiles.length} file SEP sekaligus!`);
      setBatchLog(batchSepFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: json?.msg || json?.message || 'Berhasil diimport!'
      })));

      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal fetch ke /uploaddatasepall:', err);
      setBatchProgress(100);
      setBatchStatusText(`Batch upload ${batchSepFiles.length} file SEP selesai (Demo Mode).`);
      setBatchLog(batchSepFiles.map(f => ({
        fileName: f.name,
        no_sep: f.name.replace(/\.[^/.]+$/, ""),
        status: 'success',
        msg: `[Demo Mode] Terunggah ke /uploaddatasepall`
      })));
      setTimeout(() => {
        fetchTagihan(periode1, periode2, jenis);
      }, 1200);
    } finally {
      setIsBatchUploading(false);
    }
  };

  const handleSepFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (!selected.name.toLowerCase().endsWith('.pdf') && selected.type !== 'application/pdf') {
        setUploadFeedbackSep({ type: 'danger', text: 'Hanya file PDF (.pdf) yang diperbolehkan!' });
        setSepFile(null);
        return;
      }
      setSepFile(selected);
      setUploadProgressSep(0);
      setUploadFeedbackSep(null);
    }
  };

  const handleSepUploadSubmit = async (e) => {
    e.preventDefault();
    if (!sepFile) {
      setUploadFeedbackSep({ type: 'danger', text: 'Pilih file PDF terlebih dahulu!' });
      return;
    }
    if (!sepFile.name.toLowerCase().endsWith('.pdf') && sepFile.type !== 'application/pdf') {
      setUploadFeedbackSep({ type: 'danger', text: 'Format file tidak valid! Harus file PDF (.pdf).' });
      return;
    }

    setIsUploadingSep(true);
    setUploadProgressSep(15);
    setUploadFeedbackSep(null);
    const apiUrl = getApiUrl();

    const namafile = selectedSepItem?.no_sep || (sepFile ? sepFile.name : 'file_sep.pdf');
    
    // PENTING: Backend Flask membaca request.files.getlist("file[]")
    const formData = new FormData();
    formData.append('file[]', sepFile);
    formData.append('file', sepFile);
    formData.append('file_sep', sepFile);

    if (selectedSepItem?.no_sep) {
      formData.append('no_sep', selectedSepItem.no_sep);
    }
    if (selectedSepItem?.no_medrec) {
      formData.append('no_medrec', selectedSepItem.no_medrec);
    }

    const progressInterval = setInterval(() => {
      setUploadProgressSep(prev => (prev < 85 ? prev + 15 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatasep/${encodeURIComponent(namafile)}`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgressSep(100);

      let json = {};
      try {
        json = await res.json();
      } catch (e) {
        // jika respon bukan json
      }

      if (!res.ok) {
        setUploadFeedbackSep({
          type: 'danger',
          text: json?.msg || json?.message || `Gagal upload ke server! Response HTTP ${res.status} (${res.statusText || 'Error'}).`
        });
        return;
      }

      setUploadFeedbackSep({
        type: json?.status === 1 ? 'success' : 'info',
        text: json?.msg || json?.message || `File SEP (${namafile}) berhasil diimport!`
      });
      setTimeout(() => {
        handleCloseSepUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);
    } catch (err) {
      clearInterval(progressInterval);
      console.warn(`Gagal upload ke /uploaddatasep/${namafile}:`, err);
      setUploadProgressSep(100);
      setUploadFeedbackSep({
        type: 'warning',
        text: 'Mode demo / Offline. Gagal menghubungi server.'
      });
      setTimeout(() => {
        handleCloseSepUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);
    } finally {
      setIsUploadingSep(false);
    }
  };

  const handleTagFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (!selected.name.toLowerCase().endsWith('.pdf') && selected.type !== 'application/pdf') {
        setUploadFeedbackTag({ type: 'danger', text: 'Hanya file PDF (.pdf) yang diperbolehkan!' });
        setTagFile(null);
        return;
      }
      setTagFile(selected);
      setUploadProgressTag(0);
      setUploadFeedbackTag(null);
    }
  };

  const handleTagUploadSubmit = async (e) => {
    e.preventDefault();
    if (!tagFile) {
      setUploadFeedbackTag({ type: 'danger', text: 'Pilih file PDF terlebih dahulu!' });
      return;
    }
    if (!tagFile.name.toLowerCase().endsWith('.pdf') && tagFile.type !== 'application/pdf') {
      setUploadFeedbackTag({ type: 'danger', text: 'Format file tidak valid! Harus file PDF (.pdf).' });
      return;
    }

    setIsUploadingTag(true);
    setUploadProgressTag(15);
    setUploadFeedbackTag(null);
    const apiUrl = getApiUrl();

    const namafile = selectedTagItem?.no_sep || (tagFile ? tagFile.name : 'file_tagihan.pdf');
    
    const formData = new FormData();
    formData.append('file[]', tagFile);
    formData.append('file', tagFile);
    formData.append('file_tagihan', tagFile);

    if (selectedTagItem?.no_sep) {
      formData.append('no_sep', selectedTagItem.no_sep);
    }
    if (selectedTagItem?.no_medrec) {
      formData.append('no_medrec', selectedTagItem.no_medrec);
    }

    const progressInterval = setInterval(() => {
      setUploadProgressTag(prev => (prev < 85 ? prev + 15 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatatag/${encodeURIComponent(namafile)}`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgressTag(100);

      let json = {};
      try {
        json = await res.json();
      } catch (e) {}

      if (!res.ok) {
        setUploadFeedbackTag({
          type: 'danger',
          text: json?.msg || json?.message || `Gagal upload ke server! Response HTTP ${res.status}.`
        });
        return;
      }

      setUploadFeedbackTag({
        type: json?.status === 1 ? 'success' : 'info',
        text: json?.msg || json?.message || `File Tagihan (${namafile}) berhasil diimport!`
      });
      setTimeout(() => {
        handleCloseTagUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal upload ke /uploaddatatag:', err);
      setUploadProgressTag(100);
      setUploadFeedbackTag({
        type: 'warning',
        text: 'Mode demo / Offline. Gagal menghubungi server.'
      });
      setTimeout(() => {
        handleCloseTagUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);
    } finally {
      setIsUploadingTag(false);
    }
  };

  const handleInaFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (!selected.name.toLowerCase().endsWith('.pdf') && selected.type !== 'application/pdf') {
        setUploadFeedbackIna({ type: 'danger', text: 'Hanya file PDF (.pdf) yang diperbolehkan!' });
        setInaFile(null);
        return;
      }
      setInaFile(selected);
      setUploadProgressIna(0);
      setUploadFeedbackIna(null);
    }
  };

  const handleInaUploadSubmit = async (e) => {
    e.preventDefault();
    if (!inaFile) {
      setUploadFeedbackIna({ type: 'danger', text: 'Pilih file PDF terlebih dahulu!' });
      return;
    }
    if (!inaFile.name.toLowerCase().endsWith('.pdf') && inaFile.type !== 'application/pdf') {
      setUploadFeedbackIna({ type: 'danger', text: 'Format file tidak valid! Harus file PDF (.pdf).' });
      return;
    }

    setIsUploadingIna(true);
    setUploadProgressIna(15);
    setUploadFeedbackIna(null);
    const apiUrl = getApiUrl();

    const namafile = selectedInaItem?.no_sep || (inaFile ? inaFile.name : 'file_ina.pdf');
    
    const formData = new FormData();
    formData.append('file[]', inaFile);
    formData.append('file', inaFile);
    formData.append('file_inacbg', inaFile);

    if (selectedInaItem?.no_sep) {
      formData.append('no_sep', selectedInaItem.no_sep);
    }
    if (selectedInaItem?.no_medrec) {
      formData.append('no_medrec', selectedInaItem.no_medrec);
    }

    const progressInterval = setInterval(() => {
      setUploadProgressIna(prev => (prev < 85 ? prev + 15 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddataina/${encodeURIComponent(namafile)}`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgressIna(100);

      let json = {};
      try {
        json = await res.json();
      } catch (e) {}

      if (!res.ok) {
        setUploadFeedbackIna({
          type: 'danger',
          text: json?.msg || json?.message || `Gagal upload ke server! Response HTTP ${res.status}.`
        });
        return;
      }

      setUploadFeedbackIna({
        type: json?.status === 1 ? 'success' : 'info',
        text: json?.msg || json?.message || `File INA-CBGs (${namafile}) berhasil diimport!`
      });
      setTimeout(() => {
        handleCloseInaUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal upload ke /uploaddataina:', err);
      setUploadProgressIna(100);
      setUploadFeedbackIna({
        type: 'warning',
        text: 'Mode demo / Offline. Gagal menghubungi server.'
      });
      setTimeout(() => {
        handleCloseInaUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);
    } finally {
      setIsUploadingIna(false);
    }
  };

  const handleLabFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (!selected.name.toLowerCase().endsWith('.pdf') && selected.type !== 'application/pdf') {
        setUploadFeedbackLab({ type: 'danger', text: 'Hanya file PDF (.pdf) yang diperbolehkan!' });
        setLabFile(null);
        return;
      }
      setLabFile(selected);
      setUploadProgressLab(0);
      setUploadFeedbackLab(null);
    }
  };

  const handleLabUploadSubmit = async (e) => {
    e.preventDefault();
    if (!labFile) {
      setUploadFeedbackLab({ type: 'danger', text: 'Pilih file PDF terlebih dahulu!' });
      return;
    }
    if (!labFile.name.toLowerCase().endsWith('.pdf') && labFile.type !== 'application/pdf') {
      setUploadFeedbackLab({ type: 'danger', text: 'Format file tidak valid! Harus file PDF (.pdf).' });
      return;
    }

    setIsUploadingLab(true);
    setUploadProgressLab(15);
    setUploadFeedbackLab(null);
    const apiUrl = getApiUrl();

    const namafile = selectedLabItem?.no_sep || (labFile ? labFile.name : 'file_lab.pdf');
    
    const formData = new FormData();
    formData.append('file[]', labFile);
    formData.append('file', labFile);
    formData.append('file_lab', labFile);

    if (selectedLabItem?.no_sep) {
      formData.append('no_sep', selectedLabItem.no_sep);
    }
    if (selectedLabItem?.no_medrec) {
      formData.append('no_medrec', selectedLabItem.no_medrec);
    }

    const progressInterval = setInterval(() => {
      setUploadProgressLab(prev => (prev < 85 ? prev + 15 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatalab/${encodeURIComponent(namafile)}`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgressLab(100);

      let json = {};
      try {
        json = await res.json();
      } catch (e) {}

      if (!res.ok) {
        setUploadFeedbackLab({
          type: 'danger',
          text: json?.msg || json?.message || `Gagal upload ke server! Response HTTP ${res.status}.`
        });
        return;
      }

      setUploadFeedbackLab({
        type: json?.status === 1 ? 'success' : 'info',
        text: json?.msg || json?.message || `File Laboratorium (${namafile}) berhasil diimport!`
      });
      setTimeout(() => {
        handleCloseLabUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal upload ke /uploaddatalab:', err);
      setUploadProgressLab(100);
      setUploadFeedbackLab({
        type: 'warning',
        text: 'Mode demo / Offline. Gagal menghubungi server.'
      });
      setTimeout(() => {
        handleCloseLabUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);
    } finally {
      setIsUploadingLab(false);
    }
  };

  const handleRadFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (!selected.name.toLowerCase().endsWith('.pdf') && selected.type !== 'application/pdf') {
        setUploadFeedbackRad({ type: 'danger', text: 'Hanya file PDF (.pdf) yang diperbolehkan!' });
        setRadFile(null);
        return;
      }
      setRadFile(selected);
      setUploadProgressRad(0);
      setUploadFeedbackRad(null);
    }
  };

  const handleRadUploadSubmit = async (e) => {
    e.preventDefault();
    if (!radFile) {
      setUploadFeedbackRad({ type: 'danger', text: 'Pilih file PDF terlebih dahulu!' });
      return;
    }
    if (!radFile.name.toLowerCase().endsWith('.pdf') && radFile.type !== 'application/pdf') {
      setUploadFeedbackRad({ type: 'danger', text: 'Format file tidak valid! Harus file PDF (.pdf).' });
      return;
    }

    setIsUploadingRad(true);
    setUploadProgressRad(15);
    setUploadFeedbackRad(null);
    const apiUrl = getApiUrl();

    const namafile = selectedRadItem?.no_sep || (radFile ? radFile.name : 'file_rad.pdf');
    
    const formData = new FormData();
    formData.append('file[]', radFile);
    formData.append('file', radFile);
    formData.append('file_rad', radFile);

    if (selectedRadItem?.no_sep) {
      formData.append('no_sep', selectedRadItem.no_sep);
    }
    if (selectedRadItem?.no_medrec) {
      formData.append('no_medrec', selectedRadItem.no_medrec);
    }

    const progressInterval = setInterval(() => {
      setUploadProgressRad(prev => (prev < 85 ? prev + 15 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatarad/${encodeURIComponent(namafile)}`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgressRad(100);

      let json = {};
      try {
        json = await res.json();
      } catch (e) {}

      if (!res.ok) {
        setUploadFeedbackRad({
          type: 'danger',
          text: json?.msg || json?.message || `Gagal upload ke server! Response HTTP ${res.status}.`
        });
        return;
      }

      setUploadFeedbackRad({
        type: json?.status === 1 ? 'success' : 'info',
        text: json?.msg || json?.message || `File Radiologi (${namafile}) berhasil diimport!`
      });
      setTimeout(() => {
        handleCloseRadUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal upload ke /uploaddatarad:', err);
      setUploadProgressRad(100);
      setUploadFeedbackRad({
        type: 'warning',
        text: 'Mode demo / Offline. Gagal menghubungi server.'
      });
      setTimeout(() => {
        handleCloseRadUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);
    } finally {
      setIsUploadingRad(false);
    }
  };

  const handleLainFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (!selected.name.toLowerCase().endsWith('.pdf') && selected.type !== 'application/pdf') {
        setUploadFeedbackLain({ type: 'danger', text: 'Hanya file PDF (.pdf) yang diperbolehkan!' });
        setLainFile(null);
        return;
      }
      setLainFile(selected);
      setUploadProgressLain(0);
      setUploadFeedbackLain(null);
    }
  };

  const handleLainUploadSubmit = async (e) => {
    e.preventDefault();
    if (!lainFile) {
      setUploadFeedbackLain({ type: 'danger', text: 'Pilih file PDF terlebih dahulu!' });
      return;
    }
    if (!lainFile.name.toLowerCase().endsWith('.pdf') && lainFile.type !== 'application/pdf') {
      setUploadFeedbackLain({ type: 'danger', text: 'Format file tidak valid! Harus file PDF (.pdf).' });
      return;
    }

    setIsUploadingLain(true);
    setUploadProgressLain(15);
    setUploadFeedbackLain(null);
    const apiUrl = getApiUrl();

    const namafile = selectedLainItem?.no_sep || (lainFile ? lainFile.name : 'file_lain.pdf');
    
    const formData = new FormData();
    formData.append('file[]', lainFile);
    formData.append('file', lainFile);
    formData.append('file_lain', lainFile);

    if (selectedLainItem?.no_sep) {
      formData.append('no_sep', selectedLainItem.no_sep);
    }
    if (selectedLainItem?.no_medrec) {
      formData.append('no_medrec', selectedLainItem.no_medrec);
    }

    const progressInterval = setInterval(() => {
      setUploadProgressLain(prev => (prev < 85 ? prev + 15 : prev));
    }, 150);

    try {
      const res = await fetch(`${apiUrl}/uploaddatalain/${encodeURIComponent(namafile)}`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgressLain(100);

      let json = {};
      try {
        json = await res.json();
      } catch (e) {}

      if (!res.ok) {
        setUploadFeedbackLain({
          type: 'danger',
          text: json?.msg || json?.message || `Gagal upload ke server! Response HTTP ${res.status}.`
        });
        return;
      }

      setUploadFeedbackLain({
        type: json?.status === 1 ? 'success' : 'info',
        text: json?.msg || json?.message || `File Dokumen Lain-lain (${namafile}) berhasil diimport!`
      });
      setTimeout(() => {
        handleCloseLainUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);

    } catch (err) {
      clearInterval(progressInterval);
      console.warn('Gagal upload ke /uploaddatalain:', err);
      setUploadProgressLain(100);
      setUploadFeedbackLain({
        type: 'warning',
        text: 'Mode demo / Offline. Gagal menghubungi server.'
      });
      setTimeout(() => {
        handleCloseLainUploadModal();
        fetchTagihan(periode1, periode2, jenis);
      }, 1500);
    } finally {
      setIsUploadingLain(false);
    }
  };

  // ── Filter & Pagination ──
  const [searchQuery, setSearchQuery]   = useState('');
  const [filterFlagTagihan, setFilterFlagTagihan] = useState('');
  const [filterFlagInacbgs, setFilterFlagInacbgs] = useState('');
  const [currentPage, setCurrentPage]   = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ── Mock Fallback ──
  const mockTagihan = [
    {
      flag_gabung: "X", flag_lain: "X", flaginacbgs: "X", flagrad: "X", flagtagihan: "X",
      inacbgs: "fa fa-download", lab: "fa fa-download", lain: null,
      no_medrec: "338435", no_sep: "0117R0750626V004894",
      rad: "fa fa-download", sep: "fa fa-download", tagihan: "fa fa-download",
      tarifinacbgs: "fa fa-cogs", tgl_berobat: "20 Jun 2026", xnmpasien: "IRMA WINDA YOSIDA"
    },
    {
      flag_gabung: "X", flag_lain: "X", flaginacbgs: "X", flagrad: "X", flagtagihan: "X",
      inacbgs: "fa fa-download", lab: "fa fa-download", lain: null,
      no_medrec: "383333", no_sep: "0117R0750626V005778",
      rad: "fa fa-download", sep: "fa fa-download", tagihan: "fa fa-download",
      tarifinacbgs: "fa fa-cogs", tgl_berobat: "24 Jun 2026", xnmpasien: "EGA QORI KURNIA SARI"
    },
  ];

  const formatYmdToDdmmyyyy = (ymd) => {
    if (!ymd || !ymd.includes('-')) return ymd;
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  };

  const fetchTagihan = async (p1 = periode1, p2 = periode2, j = jenis) => {
    setIsLoading(true);
    setFetchError('');
    const apiUrl = getApiUrl();
    try {
      const res = await fetch(`${apiUrl}/show_datatagihan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          periode1: formatYmdToDdmmyyyy(p1),
          periode2: formatYmdToDdmmyyyy(p2),
          jenis: j,
        }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const json = await res.json();
      setTagihanList(json?.list_tagihan || mockTagihan);
    } catch (err) {
      console.warn('Gagal /show_datatagihan:', err);
      setTagihanList(mockTagihan);
      setFetchError('Menggunakan data lokal. Gagal terhubung ke server backend.');
    } finally {
      setIsLoading(false);
    }
  };

  const [isMergingAll, setIsMergingAll] = useState(false);
  const [showConfirmMergeAllModal, setShowConfirmMergeAllModal] = useState(false);
  const [gabungAllFeedback, setGabungAllFeedback] = useState(null);

  const [isExporting, setIsExporting] = useState(false);
  const [showConfirmExportModal, setShowConfirmExportModal] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportProgress(10); // Start progress at 10% for request initialisation
    setGabungAllFeedback(null);
    const apiUrl = getApiUrl();
    try {
      const res = await fetch(`${apiUrl}/buatexceltagihan2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          periode1: formatYmdToDdmmyyyy(periode1),
          periode2: formatYmdToDdmmyyyy(periode2),
          jenis: jenis,
        }),
      });

      const contentType = res.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        setExportProgress(100);
        const json = await res.json();
        
        if (json.status === 1 || json.status === true) {
          // Ambil URL download langsung dari respons JSON
          let downloadUrl = json.download_url;
          
          if (!downloadUrl) {
            // Fallback (jika download_url kosong) cari URL download dari msg (window.location.href='...')
            const urlMatch = /window\.location\.href=['"]([^'"]+)['"]/.exec(json.msg || '');
            if (urlMatch && urlMatch[1]) {
              downloadUrl = urlMatch[1];
            } else {
              const hrefMatch = /href=['"]([^'"]+)['"]/.exec(json.msg || '');
              if (hrefMatch && hrefMatch[1]) {
                downloadUrl = hrefMatch[1];
              }
            }
          }
          
          if (downloadUrl) {
            // Jika URL adalah path relatif ke backend, tambahkan apiUrl
            if (downloadUrl.startsWith('/')) {
              downloadUrl = `${apiUrl}${downloadUrl}`;
            }
            
            // Trigger download via anchor element
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.target = '_blank';
            a.download = 'rekapklaimtanpapdf.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            
            setGabungAllFeedback({ type: 'success', text: 'Data Excel berhasil diunduh!' });
          } else {
            // Cukup tampilkan pesan jika URL tidak ditemukan
            setGabungAllFeedback({ type: 'success', text: 'Data Excel berhasil diproses.' });
          }
        } else {
          // Handle error (status 0)
          throw new Error(json.msg || json.message || json.error || 'Terjadi kesalahan saat mengunduh Excel.');
        }
      } else {
        if (!res.ok) throw new Error('Gagal mengunduh file Excel dari server.');
        
        // Baca data dengan stream untuk progress bar
        const reader = res.body.getReader();
        const contentLength = +res.headers.get('Content-Length');
        
        let receivedLength = 0;
        let chunks = [];
        
        while(true) {
          const {done, value} = await reader.read();
          if (done) {
            setExportProgress(100);
            break;
          }
          chunks.push(value);
          receivedLength += value.length;
          
          if (contentLength) {
            const percentage = Math.round((receivedLength / contentLength) * 90) + 10;
            setExportProgress(percentage);
          } else {
            setExportProgress(prev => prev < 90 ? prev + 5 : 90);
          }
        }

        const blob = new Blob(chunks, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Coba ambil nama file dari header Content-Disposition jika ada, jika tidak gunakan default dari user
        const disposition = res.headers.get('Content-Disposition');
        let filename = 'rekapklaimtanpapdf.xlsx';
        if (disposition && disposition.indexOf('attachment') !== -1) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
          if (matches != null && matches[1]) { 
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setGabungAllFeedback({ type: 'success', text: 'Data Excel berhasil diunduh!' });
      }
    } catch (err) {
      console.error('Gagal export excel:', err);
      setGabungAllFeedback({ type: 'danger', text: err.message || 'Terjadi kesalahan saat mengunduh data Excel.' });
    } finally {
      setTimeout(() => {
        setIsExporting(false);
        setShowConfirmExportModal(false);
        setExportProgress(0);
      }, 500);
      setTimeout(() => setGabungAllFeedback(null), 5000);
    }
  };

  const handleMergeAllPdf = async () => {
    setShowConfirmMergeAllModal(false);
    setIsMergingAll(true);
    setGabungAllFeedback(null);
    const apiUrl = getApiUrl();
    try {
      const res = await fetch(`${apiUrl}/mergerpdfall`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          periode1: formatYmdToDdmmyyyy(periode1),
          periode2: formatYmdToDdmmyyyy(periode2),
          jenis: jenis,
        }),
      });
      const json = await res.json();
      setGabungAllFeedback({ type: 'success', text: json?.message || 'Proses penggabungan PDF berhasil diajukan!' });
      
      // Auto refresh tabel setelah sukses
      fetchTagihan(periode1, periode2, jenis);
    } catch (err) {
      console.error('Gagal merge all pdf:', err);
      setGabungAllFeedback({ type: 'danger', text: 'Terjadi kesalahan saat menghubungi server untuk menggabungkan PDF.' });
    } finally {
      setIsMergingAll(false);
      setTimeout(() => setGabungAllFeedback(null), 5000);
    }
  };

  const [isExportingTagihan, setIsExportingTagihan] = useState(false);
  const [showConfirmExportTagihanModal, setShowConfirmExportTagihanModal] = useState(false);
  const [exportTagihanProgress, setExportTagihanProgress] = useState(0);

  const handleExportTagihanExcel = async () => {
    setIsExportingTagihan(true);
    setExportTagihanProgress(10);
    setGabungAllFeedback(null);
    const apiUrl = getApiUrl();
    try {
      const res = await fetch(`${apiUrl}/buatexceltagihan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          periode1: formatYmdToDdmmyyyy(periode1),
          periode2: formatYmdToDdmmyyyy(periode2),
          jenis: jenis,
        }),
      });

      const contentType = res.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        setExportTagihanProgress(100);
        const json = await res.json();
        
        if (json.status === 1 || json.status === true) {
          let downloadUrl = json.download_url;
          if (!downloadUrl) {
            const urlMatch = /window\.location\.href=['"]([^'"]+)['"]/.exec(json.msg || '');
            if (urlMatch && urlMatch[1]) { downloadUrl = urlMatch[1]; }
            else {
              const hrefMatch = /href=['"]([^'"]+)['"]/.exec(json.msg || '');
              if (hrefMatch && hrefMatch[1]) { downloadUrl = hrefMatch[1]; }
            }
          }
          
          if (downloadUrl) {
            if (downloadUrl.startsWith('/')) { downloadUrl = `${apiUrl}${downloadUrl}`; }
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.target = '_blank';
            a.download = 'buatexceltagihan.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setGabungAllFeedback({ type: 'success', text: 'Data Excel Tagihan berhasil diunduh!' });
          } else {
            setGabungAllFeedback({ type: 'success', text: 'Data Excel Tagihan berhasil diproses.' });
          }
        } else {
          throw new Error(json.msg || json.message || json.error || 'Terjadi kesalahan saat mengunduh Excel.');
        }
      } else {
        if (!res.ok) throw new Error('Gagal mengunduh file Excel dari server.');
        
        const reader = res.body.getReader();
        const contentLength = +res.headers.get('Content-Length');
        let receivedLength = 0;
        let chunks = [];
        
        while(true) {
          const {done, value} = await reader.read();
          if (done) {
            setExportTagihanProgress(100);
            break;
          }
          chunks.push(value);
          receivedLength += value.length;
          
          if (contentLength) {
            setExportTagihanProgress(Math.round((receivedLength / contentLength) * 90) + 10);
          } else {
            setExportTagihanProgress(prev => prev < 90 ? prev + 5 : 90);
          }
        }

        const blob = new Blob(chunks, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        const disposition = res.headers.get('Content-Disposition');
        let filename = 'buatexceltagihan.xlsx';
        if (disposition && disposition.indexOf('attachment') !== -1) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
          if (matches != null && matches[1]) { 
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setGabungAllFeedback({ type: 'success', text: 'Data Excel Tagihan berhasil diunduh!' });
      }
    } catch (err) {
      console.error('Gagal export excel:', err);
      setGabungAllFeedback({ type: 'danger', text: err.message || 'Terjadi kesalahan saat mengunduh data Excel Tagihan.' });
    } finally {
      setTimeout(() => {
        setIsExportingTagihan(false);
        setShowConfirmExportTagihanModal(false);
        setExportTagihanProgress(0);
      }, 500);
      setTimeout(() => setGabungAllFeedback(null), 5000);
    }
  };

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID') + ' WIB',
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    fetchTagihan();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Filter ──
  const filtered = tagihanList.filter(item => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      (item.xnmpasien || '').toLowerCase().includes(q) ||
      (item.no_sep    || '').toLowerCase().includes(q) ||
      (item.no_medrec || '').toLowerCase().includes(q);
    const matchTagihan  = filterFlagTagihan  ? item.flagtagihan  === filterFlagTagihan  : true;
    const matchInacbgs  = filterFlagInacbgs  ? item.flaginacbgs  === filterFlagInacbgs  : true;
    return matchSearch && matchTagihan && matchInacbgs;
  });

  const indexOfLast  = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages   = Math.ceil(filtered.length / itemsPerPage);

  // ── Handler Proses Dokumen / Endpoint uploaddatatag ──
  const handleProsesDokumen = async (label, item) => {
    if (label === 'SEP') {
      handleOpenSepUploadModal(item);
      return;
    }
    const apiUrl = getApiUrl();
    const noSep = item?.no_sep || '';

    let endpoint = '';
    if (label === 'Tagihan') {
      endpoint = `${apiUrl}/uploaddatatag/${encodeURIComponent(noSep)}`;
    } else if (label === 'INA-CBGs') {
      endpoint = `${apiUrl}/prosesinacbgs`;
    } else if (label === 'Tarif INA-CBGs') {
      endpoint = `${apiUrl}/prosestarifinacbgs`;
    } else if (label === 'Laboratorium') {
      endpoint = `${apiUrl}/proseslab`;
    } else if (label === 'Radiologi') {
      endpoint = `${apiUrl}/prosesrad`;
    } else if (label === 'Gabung') {
      endpoint = `${apiUrl}/gabungpdf/${encodeURIComponent(noSep)}`;
    } else {
      endpoint = `${apiUrl}/uploaddatatag/${encodeURIComponent(noSep)}`;
    }

    try {
      let res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          no_sep: item?.no_sep,
          no_medrec: item?.no_medrec,
          xnmpasien: item?.xnmpasien,
          tgl_berobat: item?.tgl_berobat,
        }),
      });

      if (!res.ok && label === 'Tagihan') {
        // Fallback ke /uploaddatatag tanpa parameter URL jika 404/error
        res = await fetch(`${apiUrl}/uploaddatatag`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            no_sep: item?.no_sep,
            no_medrec: item?.no_medrec,
            xnmpasien: item?.xnmpasien,
            tgl_berobat: item?.tgl_berobat,
          }),
        });
      }

      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      if (json?.url || json?.pdf_url || json?.file_url) {
        window.open(json.url || json.pdf_url || json.file_url, '_blank');
      } else {
        alert(json?.msg || json?.message || `Proses ${label} berhasil diproses via uploaddatatag (No. SEP: ${item?.no_sep})`);
      }
    } catch (err) {
      console.warn(`Gagal memproses ${label} ke ${endpoint}:`, err);
    }
  };

  // ── Helper render tombol dokumen / icon checklist 'V' / gear 'X' ──
  const DocButton = ({ value, flagValue, label, color, tip, item, onClick }) => {
    const activeVal = flagValue || value;

    const handleClick = () => {
      if (onClick) onClick();
      else if (item) handleProsesDokumen(label, item);
      else alert(`[DEMO] Membuka dokumen: ${label}`);
    };

    // Jika nilainya adalah 'V', tampilkan icon checklist hijau
    if (activeVal === 'V' || value === 'V') {
      return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{label}: Checklist / Verifikasi (V)</Tooltip>}>
          <span
            className="d-inline-flex align-items-center justify-content-center text-success"
            style={{ cursor: 'pointer' }}
            onClick={handleClick}
          >
            <i className="bi bi-check-circle-fill fs-5" style={{ color: '#16a34a' }}></i>
          </span>
        </OverlayTrigger>
      );
    }
    // Jika nilainya adalah 'X', tampilkan icon gear
    if (activeVal === 'X' || value === 'X') {
      return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{label}: Gear / Proses (X)</Tooltip>}>
          <span
            className="d-inline-flex align-items-center justify-content-center text-warning"
            style={{ cursor: 'pointer' }}
            onClick={handleClick}
          >
            <i className="bi bi-gear-fill fs-5" style={{ color: '#d97706' }}></i>
          </span>
        </OverlayTrigger>
      );
    }
    const icon = resolveIcon(value);
    if (!icon) return (
      <span className="text-muted" style={{ fontSize: '0.68rem' }}>—</span>
    );
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>{tip || label}</Tooltip>}>
        <Button
          size="sm"
          variant={`outline-${color}`}
          style={{ padding: '0.15rem 0.45rem', fontSize: '0.72rem', borderRadius: '6px' }}
          onClick={handleClick}
        >
          <i className={icon}></i>
        </Button>
      </OverlayTrigger>
    );
  };

  // ── Helper render badge/icon flag ──
  // eslint-disable-next-line no-unused-vars
  const renderFlag = (val) => {
    if (val === 'V') {
      return (
        <OverlayTrigger placement="top" overlay={<Tooltip>Checklist / Verifikasi (V)</Tooltip>}>
          <span className="d-inline-flex align-items-center justify-content-center text-success">
            <i className="bi bi-check-circle-fill fs-5" style={{ color: '#16a34a' }}></i>
          </span>
        </OverlayTrigger>
      );
    }
    if (val === 'X') {
      return (
        <OverlayTrigger placement="top" overlay={<Tooltip>Gear / Proses (X)</Tooltip>}>
          <span className="d-inline-flex align-items-center justify-content-center text-warning">
            <i className="bi bi-gear-fill fs-5" style={{ color: '#d97706' }}></i>
          </span>
        </OverlayTrigger>
      );
    }
    if (!val) {
      return <span className="text-muted" style={{ fontSize: '0.68rem' }}>—</span>;
    }
    return <Badge bg="secondary" style={{ fontSize: '0.65rem', borderRadius: '20px' }}>{val}</Badge>;
  };

  return (
    <div style={styles.appContainer}>

      {/* ══ NAVBAR ══ */}
      <nav className="navbar navbar-expand-xl navbar-dark shadow-sm" style={styles.navbarCustom}>
        <Container fluid>
          <a className="navbar-brand d-flex align-items-center me-3" href="#home"
            onClick={e => { e.preventDefault(); navigate('/home'); }}>
            <div className="d-flex align-items-center justify-content-center me-2 rounded bg-white bg-opacity-10 p-1">
              <i className="bi bi-shield-plus text-warning fs-4"></i>
            </div>
            <div>
              <div className="fw-bold text-white lh-1" style={{ fontSize: '0.95rem' }}>
                SIMRS2 <span style={{ fontSize: '0.78rem', color: '#2dd4bf' }}>Finance</span>
              </div>
              <div className="text-white-50" style={{ fontSize: '0.62rem' }}>Sistem Informasi Rumah Sakit</div>
            </div>
          </a>
          <div className="d-flex align-items-center gap-3 ms-auto">
            <div className="small border-end pe-3 border-light border-opacity-25 d-none d-sm-block">
              <div className="fw-semibold text-white-50" style={{ fontSize: '0.73rem' }}>{time.date}</div>
              <div className="fw-bold" style={{ fontSize: '0.78rem', color: '#2dd4bf' }}>{time.clock}</div>
            </div>
            <Button variant="light" size="sm" onClick={() => navigate('/home')} className="d-flex align-items-center gap-1">
              <i className="bi bi-house-door-fill" style={{ color: '#0d9488' }}></i>
              <span>Kembali</span>
            </Button>
          </div>
        </Container>
      </nav>

      {/* ══ MAIN CONTENT ══ */}
      <div style={styles.mainContent}>

        {/* ── Stats Summary Bar ── */}
        <Row className="g-2 mb-2">
          {[
            { label: 'Total Tagihan', val: tagihanList.length, icon: 'bi bi-receipt', color: '#0d9488', bg: '#f0fdfa' },
            { label: 'Tagihan Tersedia', val: tagihanList.filter(t => t.flagtagihan === 'X').length, icon: 'bi bi-cloud-download-fill', color: '#2563eb', bg: '#eff6ff' },
            { label: 'INA-CBGs Tersedia', val: tagihanList.filter(t => t.flaginacbgs === 'X').length, icon: 'bi bi-file-earmark-medical-fill', color: '#7c3aed', bg: '#f5f3ff' },
            { label: 'Rad Tersedia', val: tagihanList.filter(t => t.flagrad === 'X').length, icon: 'bi bi-radioactive', color: '#d97706', bg: '#fffbeb' },
          ].map((s, i) => (
            <Col md={3} key={i}>
              <div style={{ background: s.bg, border: `1.5px solid ${s.color}22`, borderRadius: '10px', padding: '0.35rem 0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ background: s.color, borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={s.icon} style={{ color: '#fff', fontSize: '1rem' }}></i>
                </div>
                <div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem', color: s.color, lineHeight: 1 }}>{s.val}</div>
                  <div className="text-muted" style={{ fontSize: '0.68rem' }}>{s.label}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* ── Main Card ── */}
        <Card style={styles.mainCard} className="shadow-sm border-0">
          {/* Card Header */}
          <div style={{ ...styles.cardHeader, ...styles.bgMenuHeader }}
            className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span className="fw-bold">
              <i className="bi bi-receipt-cutoff me-1 text-warning"></i>
              Daftar Penagihan BPJS — Data Tagihan PDF
            </span>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <DropdownButton
                variant="light"
                size="sm"
                title={<span className="d-flex align-items-center justify-content-center" style={{ minWidth: '115px' }}><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#0284c7', fontSize: '0.85rem' }}></i> <span className="fw-bold" style={{ color: '#0f172a', fontSize: '0.73rem' }}>Menu Upload</span></span>}
                className="shadow-sm"
              >
                <Dropdown.Item onClick={handleOpenUploadModal} className="fw-semibold bg-light mb-1" style={{ fontSize: '0.8rem', color: '#d97706', borderBottom: '1px solid #e2e8f0' }}>
                  <i className="bi bi-file-earmark-excel-fill me-2 text-warning"></i>Upload NCC BPJS
                </Dropdown.Item>
                <Dropdown.Item onClick={handleOpenBatchSepModal} className="fw-semibold" style={{ fontSize: '0.8rem', color: '#059669' }}>
                  <i className="bi bi-folder-symlink-fill me-2"></i>Upload All SEP
                </Dropdown.Item>
                <Dropdown.Item onClick={handleOpenBatchTagihanModal} className="fw-semibold" style={{ fontSize: '0.8rem', color: '#0284c7' }}>
                  <i className="bi bi-file-earmark-pdf-fill me-2"></i>Upload All Tagihan
                </Dropdown.Item>
                <Dropdown.Item onClick={handleOpenBatchInacbgsModal} className="fw-semibold" style={{ fontSize: '0.8rem', color: '#e11d48' }}>
                  <i className="bi bi-file-earmark-medical-fill me-2"></i>Upload All INA-CBGS
                </Dropdown.Item>
                <Dropdown.Item onClick={handleOpenBatchLabModal} className="fw-semibold" style={{ fontSize: '0.8rem', color: '#0891b2' }}>
                  <i className="bi bi-file-earmark-bar-graph-fill me-2"></i>Upload All Laboratorium
                </Dropdown.Item>
                <Dropdown.Item onClick={handleOpenBatchRadModal} className="fw-semibold" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  <i className="bi bi-file-earmark-image-fill me-2"></i>Upload All Radiologi
                </Dropdown.Item>
                <Dropdown.Item onClick={handleOpenBatchLainModal} className="fw-semibold" style={{ fontSize: '0.8rem', color: '#334155' }}>
                  <i className="bi bi-file-earmark-medical me-2"></i>Upload All Lain-lain
                </Dropdown.Item>
              </DropdownButton>

              <DropdownButton
                variant="success"
                size="sm"
                title={
                  <span className="d-flex align-items-center justify-content-center" style={{ minWidth: '110px' }}>
                    {(isExporting || isExportingTagihan) ? <Spinner size="sm" animation="border" className="me-1" style={{ width: '0.8rem', height: '0.8rem' }} /> : <i className="bi bi-file-earmark-spreadsheet-fill me-1 text-white" style={{ fontSize: '0.85rem' }}></i>}
                    <span className="fw-bold text-white" style={{ fontSize: '0.73rem' }}>Unduh Excel</span>
                  </span>
                }
                disabled={isExporting || isExportingTagihan || isLoading}
                className="shadow-sm"
              >
                <Dropdown.Item onClick={() => setShowConfirmExportModal(true)} className="fw-semibold" style={{ fontSize: '0.8rem', color: '#10b981' }}>
                  <i className="bi bi-file-earmark-spreadsheet-fill me-2"></i>Rekap Klaim Tanpa PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowConfirmExportTagihanModal(true)} className="fw-semibold" style={{ fontSize: '0.8rem', color: '#0284c7' }}>
                  <i className="bi bi-file-earmark-spreadsheet me-2"></i>Buat Excel Tagihan
                </Dropdown.Item>
              </DropdownButton>

              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowConfirmMergeAllModal(true)}
                disabled={isMergingAll || isLoading}
                className="fw-bold d-flex align-items-center justify-content-center gap-1 text-white shadow-sm"
                style={{ fontSize: '0.73rem', background: '#e11d48', borderColor: '#e11d48', minWidth: '125px' }}
              >
                {isMergingAll ? <Spinner size="sm" animation="border" style={{ width: '0.8rem', height: '0.8rem' }} /> : <i className="bi bi-file-earmark-zip-fill" style={{ fontSize: '0.85rem' }}></i>}
                <span>Gabung All Pdf</span>
              </Button>
              <Button variant="outline-light" size="sm" onClick={() => fetchTagihan(periode1, periode2, jenis)} disabled={isLoading} className="fw-bold d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '0.73rem', minWidth: '90px' }}>
                {isLoading ? <Spinner size="sm" animation="border" style={{ width: '0.8rem', height: '0.8rem' }} /> : <i className="bi bi-arrow-clockwise" style={{ fontSize: '0.85rem' }}></i>}
                <span>Reload</span>
              </Button>
            </div>
          </div>

          <Card.Body className="p-1">

            {gabungAllFeedback && (
              <div className={`alert alert-${gabungAllFeedback.type} alert-dismissible fade show d-flex align-items-center mb-1 p-2`} role="alert" style={{ fontSize: '0.75rem', borderRadius: '4px' }}>
                <i className={`bi bi-${gabungAllFeedback.type === 'success' ? 'check-circle-fill' : 'exclamation-triangle-fill'} fs-5 me-2`}></i>
                <div>
                  <strong>{gabungAllFeedback.type === 'success' ? 'Berhasil: ' : 'Gagal: '}</strong>
                  {gabungAllFeedback.text}
                </div>
                <button type="button" className="btn-close p-2" onClick={() => setGabungAllFeedback(null)} aria-label="Close" style={{ top: '0', right: '0' }}></button>
              </div>
            )}

            {/* Filter Periode & Jenis Input (POST Body: { periode1, periode2, jenis }) */}
            <Row className="g-2 mb-1 p-1 rounded bg-light border align-items-end">
              <Col md={3} sm={4}>
                <Form.Label className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>
                  <i className="bi bi-calendar-event me-1" style={{ color: '#0d9488' }}></i>Periode 1
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={periode1}
                  onChange={e => {
                    const newVal = e.target.value;
                    setPeriode1(newVal);
                    fetchTagihan(newVal, periode2, jenis);
                  }}
                  style={{ fontSize: '0.78rem' }}
                />
              </Col>
              <Col md={3} sm={4}>
                <Form.Label className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>
                  <i className="bi bi-calendar-event me-1" style={{ color: '#0d9488' }}></i>Periode 2
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={periode2}
                  onChange={e => {
                    const newVal = e.target.value;
                    setPeriode2(newVal);
                    fetchTagihan(periode1, newVal, jenis);
                  }}
                  style={{ fontSize: '0.78rem' }}
                />
              </Col>
              <Col md={3} sm={4}>
                <Form.Label className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.73rem' }}>
                  <i className="bi bi-hospital me-1" style={{ color: '#0d9488' }}></i>Jenis Pelayanan
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={jenis}
                  onChange={e => {
                    const newVal = e.target.value;
                    setJenis(newVal);
                    fetchTagihan(periode1, periode2, newVal);
                  }}
                  style={{ fontSize: '0.78rem' }}
                >
                  <option value="RJ">Rawat Jalan (RJ)</option>
                  <option value="RI">Rawat Inap (RI)</option>
                </Form.Select>
              </Col>
              <Col md={3} sm={12}>
                <Button
                  size="sm"
                  className="w-100 fw-bold d-flex align-items-center justify-content-center gap-1"
                  style={{ background: '#0d9488', borderColor: '#0d9488', color: '#fff', fontSize: '0.75rem', height: '31px' }}
                  onClick={() => fetchTagihan(periode1, periode2, jenis)}
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner size="sm" animation="border" /> : <i className="bi bi-funnel-fill"></i>} Filter Data
                </Button>
              </Col>
            </Row>

            {/* Toolbar Search & Status Filter */}
            <Row className="g-2 mb-1">
              {/* Search */}
              <Col md={4}>
                <InputGroup size="sm">
                  <InputGroup.Text className="bg-light text-muted"><i className="bi bi-search"></i></InputGroup.Text>
                  <Form.Control type="text" placeholder="Cari nama pasien, No. SEP, No. MR..."
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    style={{ fontSize: '0.78rem' }} />
                </InputGroup>
              </Col>

              {/* Filter Flag Tagihan */}
              <Col md={2}>
                <Form.Select size="sm" value={filterFlagTagihan}
                  onChange={e => { setFilterFlagTagihan(e.target.value); setCurrentPage(1); }}
                  style={{ fontSize: '0.78rem' }}>
                  <option value="">Status Tagihan</option>
                  <option value="X">Tersedia</option>
                  <option value="">Belum</option>
                </Form.Select>
              </Col>

              {/* Filter Flag INA-CBGs */}
              <Col md={2}>
                <Form.Select size="sm" value={filterFlagInacbgs}
                  onChange={e => { setFilterFlagInacbgs(e.target.value); setCurrentPage(1); }}
                  style={{ fontSize: '0.78rem' }}>
                  <option value="">Status INA-CBGs</option>
                  <option value="X">Tersedia</option>
                </Form.Select>
              </Col>

              {/* Items per Page */}
              <Col md={2}>
                <Form.Select size="sm" value={itemsPerPage}
                  onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ fontSize: '0.78rem' }}>
                  <option value={10}>10 Baris</option>
                  <option value={20}>20 Baris</option>
                  <option value={50}>50 Baris</option>
                  <option value={100}>100 Baris</option>
                </Form.Select>
              </Col>
            </Row>

            {fetchError && (
              <div className="alert alert-warning py-1 px-3 mb-2" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>{fetchError}
              </div>
            )}

            {/* ── Legenda Ikon ── */}
            <div className="d-flex gap-3 mb-1 flex-wrap align-items-center p-1 px-2 rounded"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '0.7rem' }}>
              <span className="fw-semibold text-muted me-1">Legenda:</span>
              {[
                { icon: 'bi bi-cloud-download-fill', color: '#2563eb', label: 'Unduh Dokumen' },
                { icon: 'bi bi-gear-fill',           color: '#d97706', label: 'Kelola Tarif INA-CBGs' },
                { icon: 'bi bi-file-earmark-text-fill', color: '#0d9488', label: 'SEP' },
                { icon: 'bi bi-activity',            color: '#7c3aed', label: 'Lab / Rad' },
              ].map((l, i) => (
                <span key={i} className="d-flex align-items-center gap-1">
                  <i className={l.icon} style={{ color: l.color, fontSize: '0.85rem' }}></i>
                  <span className="text-muted">{l.label}</span>
                </span>
              ))}
            </div>

            {/* ── Tabel ── */}
            <div className="table-responsive" style={{ minHeight: '380px' }}>
              <Table responsive striped bordered hover size="sm" className="align-middle mb-0"
                style={{ fontSize: '0.76rem', minWidth: '1050px' }}>
                <thead className="sticky-top" style={{ background: '#0f172a', color: '#e2e8f0' }}>
                  <tr>
                    <th style={{ width: '35px' }} className="text-center">#</th>
                    <th style={{ width: '90px' }}>No. MR</th>
                    <th style={{ minWidth: '175px' }}>Nama Pasien</th>
                    <th style={{ width: '175px' }}>No. SEP</th>
                    <th style={{ width: '100px' }} className="text-center">Tgl. Berobat</th>
                    {/* Dokumen */}
                    <th style={{ width: '68px' }} className="text-center">
                      <i className="bi bi-file-earmark-text-fill me-1" style={{ color: '#38bdf8' }}></i>SEP
                    </th>
                    <th style={{ width: '68px' }} className="text-center">
                      <i className="bi bi-receipt-cutoff me-1" style={{ color: '#f59e0b' }}></i>Tagihan
                    </th>
                    <th style={{ width: '68px' }} className="text-center">
                      <i className="bi bi-file-earmark-medical-fill me-1" style={{ color: '#a78bfa' }}></i>INA-CBGs
                    </th>
                    <th style={{ width: '68px' }} className="text-center">
                      <i className="bi bi-activity me-1" style={{ color: '#34d399' }}></i>Lab
                    </th>
                    <th style={{ width: '68px' }} className="text-center">
                      <i className="bi bi-radioactive me-1" style={{ color: '#f87171' }}></i>Rad
                    </th>
                    <th style={{ width: '68px' }} className="text-center">
                      <i className="bi bi-three-dots me-1" style={{ color: '#94a3b8' }}></i>Lain
                    </th>
                    {/* Flag Badges */}
                    <th style={{ width: '75px' }} className="text-center">Gabung</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="13" className="text-center py-5 text-muted">
                        <Spinner animation="border" style={{ color: '#0d9488' }} className="mb-2" />
                        <div>Memuat data tagihan BPJS...</div>
                      </td>
                    </tr>
                  ) : currentItems.length > 0 ? currentItems.map((item, idx) => (
                    <tr key={idx}>
                      <td className="text-center text-muted">{indexOfFirst + idx + 1}</td>

                      {/* No MR */}
                      <td>
                        <span className="fw-bold font-monospace" style={{ color: '#0d9488', fontSize: '0.73rem' }}>
                          {item.no_medrec}
                        </span>
                      </td>

                      {/* Nama Pasien */}
                      <td className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                        {item.xnmpasien}
                      </td>

                      {/* No SEP */}
                      <td>
                        <span className="font-monospace text-muted" style={{ fontSize: '0.7rem' }}>
                          {item.no_sep}
                        </span>
                      </td>

                      {/* Tgl Berobat */}
                      <td className="text-center">
                        <Badge bg="light" text="dark" className="border" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          <i className="bi bi-calendar3 me-1 text-primary"></i>
                          {item.tgl_berobat}
                        </Badge>
                      </td>

                      {/* SEP */}
                      <td className="text-center">
                        <DocButton
                          value={item.sep}
                          item={item}
                          label="SEP"
                          color="primary"
                          tip="Upload File SEP PDF"
                          onClick={() => handleOpenSepUploadModal(item)}
                        />
                      </td>

                      {/* Tagihan */}
                      <td className="text-center">
                        <DocButton
                          value={item.tagihan}
                          flagValue={item.flagtagihan}
                          item={item}
                          label="Tagihan"
                          color="warning"
                          tip="Proses / Unduh Tagihan BPJS"
                          onClick={() => handleOpenTagUploadModal(item)}
                        />
                      </td>

                      {/* INA-CBGs */}
                      <td className="text-center">
                        <DocButton
                          value={item.inacbgs}
                          flagValue={item.flaginacbgs}
                          item={item}
                          label="INA-CBGs"
                          color="info"
                          tip="Proses / Unduh Dokumen INA-CBGs"
                          onClick={() => handleOpenInaUploadModal(item)}
                        />
                      </td>

                      {/* Lab */}
                      <td className="text-center">
                        <DocButton
                          value={item.lab}
                          item={item}
                          label="Laboratorium"
                          color="success"
                          tip="Proses / Unduh Dokumen Lab"
                          onClick={() => handleOpenLabUploadModal(item)}
                        />
                      </td>

                      {/* Rad */}
                      <td className="text-center">
                        <DocButton
                          value={item.rad}
                          flagValue={item.flagrad}
                          item={item}
                          label="Radiologi"
                          color="danger"
                          tip="Proses / Unduh Dokumen Radiologi"
                          onClick={() => handleOpenRadUploadModal(item)}
                        />
                      </td>

                      {/* Lain */}
                      <td className="text-center">
                        <DocButton
                          value={item.lain}
                          flagValue={item.flag_lain}
                          item={item}
                          label="Lain-lain"
                          color="secondary"
                          tip="Proses / Unduh Dokumen Lain"
                          onClick={() => handleOpenLainUploadModal(item)}
                        />
                      </td>

                      {/* Flag Gabung */}
                      <td className="text-center">
                        <DocButton
                          value={item.gabung}
                          flagValue={item.flag_gabung}
                          item={item}
                          label="Gabung"
                          color="dark"
                          tip="Proses / Gabung PDF"
                          onClick={() => handleOpenGabungConfirm(item)}
                        />
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="13" className="text-center py-4 text-muted">
                        Tidak ada data tagihan yang cocok dengan filter yang dipilih.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination size="sm" className="mb-0">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                  {[...Array(Math.min(totalPages, 10))].map((_, i) => (
                    <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </Pagination.Item>
                  ))}
                  {totalPages > 10 && <Pagination.Ellipsis disabled />}
                  <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                </Pagination>
              </div>
            )}

            {/* ── Footer ── */}
            <div className="d-flex justify-content-between mt-3 pt-2 border-top text-muted" style={{ fontSize: '0.72rem' }}>
              <span>
                Menampilkan{' '}
                <strong>{filtered.length > 0 ? indexOfFirst + 1 : 0}–{Math.min(indexOfLast, filtered.length)}</strong>{' '}
                dari <strong>{filtered.length}</strong> tagihan (Total: {tagihanList.length})
              </span>
              <span>IP Server: <strong>{getApiUrl()}</strong></span>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* ══ MODAL UPLOAD NCC BPJS ══ */}
      <Modal show={showUploadModal} onHide={handleCloseUploadModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-excel-fill text-success fs-5"></i>
            <span>Upload Data dari Aplikasi NCC BPJS</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleUploadSubmit}>
            <div className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                Pilih File Excel NCC BPJS (.xlsx / .xls)
              </Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                required
                style={{ fontSize: '0.78rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.68rem' }}>
                Format file yang didukung: Microsoft Excel (.xlsx, .xls) dari aplikasi NCC BPJS.
              </Form.Text>
            </div>

            {excelFile && (
              <div className="alert alert-info py-1 px-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-paperclip fs-6"></i>
                <div>
                  <strong>Terpilih:</strong> {excelFile.name} ({(excelFile.size / 1024).toFixed(1)} KB)
                </div>
              </div>
            )}

            {isUploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.73rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#0d9488' }}></i>Mengunggah & Memproses Data...</span>
                  <span className="fw-bold" style={{ color: '#0d9488' }}>{uploadProgress}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  now={uploadProgress}
                  style={{ height: '14px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {uploadFeedback && (
              <div className={`alert alert-${uploadFeedback.type} py-1 px-2 mb-3`} style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-info-circle-fill me-1"></i>
                {uploadFeedback.text}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseUploadModal} disabled={isUploading}>
                Batal
              </Button>
              <Button type="submit" variant="teal" size="sm" className="fw-bold" style={{ background: '#0d9488', borderColor: '#0d9488', color: '#fff', fontSize: '0.78rem' }} disabled={isUploading}>
                {isUploading ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Proses Import
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      {/* ══ MODAL UPLOAD FILE SEP (uploaddatasep) ══ */}
      <Modal show={showSepUploadModal} onHide={handleCloseSepUploadModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-arrow-up-fill fs-5"></i>
            <span>Upload File SEP</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleSepUploadSubmit}>
            {selectedSepItem && (
              <div className="alert alert-primary py-1 px-2 mb-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-person-badge-fill me-1"></i>
                <strong>Pasien:</strong> {selectedSepItem.xnmpasien} | <strong>No. SEP:</strong> {selectedSepItem.no_sep}
              </div>
            )}

            <div className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                Pilih File Dokumen SEP (Khusus .pdf)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                onChange={handleSepFileChange}
                required
                style={{ fontSize: '0.78rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.68rem' }}>
                Format file yang didukung: <strong>Khusus File PDF (.pdf)</strong>.
              </Form.Text>
            </div>

            {sepFile && (
              <div className="alert alert-info py-1 px-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-paperclip fs-6"></i>
                <div>
                  <strong>File Terpilih:</strong> {sepFile.name} ({(sepFile.size / 1024).toFixed(1)} KB)
                </div>
              </div>
            )}

            {isUploadingSep && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.73rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#0284c7' }}></i>Mengunggah File SEP...</span>
                  <span className="fw-bold" style={{ color: '#0284c7' }}>{uploadProgressSep}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="info"
                  now={uploadProgressSep}
                  style={{ height: '14px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {uploadFeedbackSep && (
              <div className={`alert alert-${uploadFeedbackSep.type} py-1 px-2 mb-3`} style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-info-circle-fill me-1"></i>
                {uploadFeedbackSep.text}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseSepUploadModal} disabled={isUploadingSep}>
                Batal
              </Button>
              <Button type="submit" size="sm" className="fw-bold text-white" style={{ background: '#0284c7', borderColor: '#0284c7', fontSize: '0.78rem' }} disabled={isUploadingSep}>
                {isUploadingSep ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Upload File SEP
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL UPLOAD FILE TAGIHAN (uploaddatatag) ══ */}
      <Modal show={showTagUploadModal} onHide={handleCloseTagUploadModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-arrow-up-fill fs-5"></i>
            <span>Upload File Tagihan</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleTagUploadSubmit}>
            {selectedTagItem && (
              <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-person-badge-fill me-1"></i>
                <strong>Pasien:</strong> {selectedTagItem.xnmpasien} | <strong>No. SEP:</strong> {selectedTagItem.no_sep}
              </div>
            )}

            <div className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                Pilih File Dokumen Tagihan (Khusus .pdf)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                onChange={handleTagFileChange}
                required
                style={{ fontSize: '0.78rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.68rem' }}>
                Format file yang didukung: <strong>Khusus File PDF (.pdf)</strong>.
              </Form.Text>
            </div>

            {tagFile && (
              <div className="alert alert-info py-1 px-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-paperclip fs-6"></i>
                <div>
                  <strong>File Terpilih:</strong> {tagFile.name} ({(tagFile.size / 1024).toFixed(1)} KB)
                </div>
              </div>
            )}

            {isUploadingTag && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.73rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#d97706' }}></i>Mengunggah File Tagihan...</span>
                  <span className="fw-bold" style={{ color: '#d97706' }}>{uploadProgressTag}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="warning"
                  now={uploadProgressTag}
                  style={{ height: '14px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {uploadFeedbackTag && (
              <div className={`alert alert-${uploadFeedbackTag.type} py-1 px-2 mb-3`} style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-info-circle-fill me-1"></i>
                {uploadFeedbackTag.text}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseTagUploadModal} disabled={isUploadingTag}>
                Batal
              </Button>
              <Button type="submit" size="sm" className="fw-bold text-white" style={{ background: '#d97706', borderColor: '#d97706', fontSize: '0.78rem' }} disabled={isUploadingTag}>
                {isUploadingTag ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Upload File Tagihan
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL UPLOAD FILE INA-CBGs (uploaddataina) ══ */}
      <Modal show={showInaUploadModal} onHide={handleCloseInaUploadModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-arrow-up-fill fs-5"></i>
            <span>Upload File INA-CBGs</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleInaUploadSubmit}>
            {selectedInaItem && (
              <div className="alert alert-info py-1 px-2 mb-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-person-badge-fill me-1"></i>
                <strong>Pasien:</strong> {selectedInaItem.xnmpasien} | <strong>No. SEP:</strong> {selectedInaItem.no_sep}
              </div>
            )}

            <div className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                Pilih File Dokumen INA-CBGs (Khusus .pdf)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                onChange={handleInaFileChange}
                required
                style={{ fontSize: '0.78rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.68rem' }}>
                Format file yang didukung: <strong>Khusus File PDF (.pdf)</strong>.
              </Form.Text>
            </div>

            {inaFile && (
              <div className="alert alert-primary py-1 px-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-paperclip fs-6"></i>
                <div>
                  <strong>File Terpilih:</strong> {inaFile.name} ({(inaFile.size / 1024).toFixed(1)} KB)
                </div>
              </div>
            )}

            {isUploadingIna && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.73rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#0ea5e9' }}></i>Mengunggah File INA-CBGs...</span>
                  <span className="fw-bold" style={{ color: '#0ea5e9' }}>{uploadProgressIna}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="info"
                  now={uploadProgressIna}
                  style={{ height: '14px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {uploadFeedbackIna && (
              <div className={`alert alert-${uploadFeedbackIna.type} py-1 px-2 mb-3`} style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-info-circle-fill me-1"></i>
                {uploadFeedbackIna.text}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseInaUploadModal} disabled={isUploadingIna}>
                Batal
              </Button>
              <Button type="submit" size="sm" className="fw-bold text-white" style={{ background: '#0284c7', borderColor: '#0284c7', fontSize: '0.78rem' }} disabled={isUploadingIna}>
                {isUploadingIna ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Upload File INA-CBGs
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL UPLOAD FILE LABORATORIUM (uploaddatalab) ══ */}
      <Modal show={showLabUploadModal} onHide={handleCloseLabUploadModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-arrow-up-fill fs-5"></i>
            <span>Upload File Laboratorium</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleLabUploadSubmit}>
            {selectedLabItem && (
              <div className="alert alert-success py-1 px-2 mb-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-person-badge-fill me-1"></i>
                <strong>Pasien:</strong> {selectedLabItem.xnmpasien} | <strong>No. SEP:</strong> {selectedLabItem.no_sep}
              </div>
            )}

            <div className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                Pilih File Dokumen Laboratorium (Khusus .pdf)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                onChange={handleLabFileChange}
                required
                style={{ fontSize: '0.78rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.68rem' }}>
                Format file yang didukung: <strong>Khusus File PDF (.pdf)</strong>.
              </Form.Text>
            </div>

            {labFile && (
              <div className="alert alert-info py-1 px-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-paperclip fs-6"></i>
                <div>
                  <strong>File Terpilih:</strong> {labFile.name} ({(labFile.size / 1024).toFixed(1)} KB)
                </div>
              </div>
            )}

            {isUploadingLab && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.73rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#10b981' }}></i>Mengunggah File Lab...</span>
                  <span className="fw-bold" style={{ color: '#10b981' }}>{uploadProgressLab}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="success"
                  now={uploadProgressLab}
                  style={{ height: '14px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {uploadFeedbackLab && (
              <div className={`alert alert-${uploadFeedbackLab.type} py-1 px-2 mb-3`} style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-info-circle-fill me-1"></i>
                {uploadFeedbackLab.text}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseLabUploadModal} disabled={isUploadingLab}>
                Batal
              </Button>
              <Button type="submit" size="sm" className="fw-bold text-white" style={{ background: '#059669', borderColor: '#059669', fontSize: '0.78rem' }} disabled={isUploadingLab}>
                {isUploadingLab ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Upload File Lab
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL UPLOAD FILE RADIOLOGI (uploaddatarad) ══ */}
      <Modal show={showRadUploadModal} onHide={handleCloseRadUploadModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-arrow-up-fill fs-5"></i>
            <span>Upload File Radiologi</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleRadUploadSubmit}>
            {selectedRadItem && (
              <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-person-badge-fill me-1"></i>
                <strong>Pasien:</strong> {selectedRadItem.xnmpasien} | <strong>No. SEP:</strong> {selectedRadItem.no_sep}
              </div>
            )}

            <div className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                Pilih File Dokumen Radiologi (Khusus .pdf)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                onChange={handleRadFileChange}
                required
                style={{ fontSize: '0.78rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.68rem' }}>
                Format file yang didukung: <strong>Khusus File PDF (.pdf)</strong>.
              </Form.Text>
            </div>

            {radFile && (
              <div className="alert alert-info py-1 px-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-paperclip fs-6"></i>
                <div>
                  <strong>File Terpilih:</strong> {radFile.name} ({(radFile.size / 1024).toFixed(1)} KB)
                </div>
              </div>
            )}

            {isUploadingRad && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.73rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#ef4444' }}></i>Mengunggah File Radiologi...</span>
                  <span className="fw-bold" style={{ color: '#ef4444' }}>{uploadProgressRad}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="danger"
                  now={uploadProgressRad}
                  style={{ height: '14px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {uploadFeedbackRad && (
              <div className={`alert alert-${uploadFeedbackRad.type} py-1 px-2 mb-3`} style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-info-circle-fill me-1"></i>
                {uploadFeedbackRad.text}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseRadUploadModal} disabled={isUploadingRad}>
                Batal
              </Button>
              <Button type="submit" size="sm" className="fw-bold text-white" style={{ background: '#dc2626', borderColor: '#dc2626', fontSize: '0.78rem' }} disabled={isUploadingRad}>
                {isUploadingRad ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Upload File Radiologi
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL UPLOAD FILE DOKUMEN LAIN (uploaddatalain) ══ */}
      <Modal show={showLainUploadModal} onHide={handleCloseLainUploadModal} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-arrow-up-fill fs-5"></i>
            <span>Upload File Dokumen Lain-lain</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleLainUploadSubmit}>
            {selectedLainItem && (
              <div className="alert alert-secondary py-1 px-2 mb-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-person-badge-fill me-1"></i>
                <strong>Pasien:</strong> {selectedLainItem.xnmpasien} | <strong>No. SEP:</strong> {selectedLainItem.no_sep}
              </div>
            )}

            <div className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.78rem' }}>
                Pilih File Dokumen Lain (Khusus .pdf)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                onChange={handleLainFileChange}
                required
                style={{ fontSize: '0.78rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.68rem' }}>
                Format file yang didukung: <strong>Khusus File PDF (.pdf)</strong>.
              </Form.Text>
            </div>

            {lainFile && (
              <div className="alert alert-info py-1 px-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-paperclip fs-6"></i>
                <div>
                  <strong>File Terpilih:</strong> {lainFile.name} ({(lainFile.size / 1024).toFixed(1)} KB)
                </div>
              </div>
            )}

            {isUploadingLain && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.73rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#64748b' }}></i>Mengunggah File Lain-lain...</span>
                  <span className="fw-bold" style={{ color: '#64748b' }}>{uploadProgressLain}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="secondary"
                  now={uploadProgressLain}
                  style={{ height: '14px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {uploadFeedbackLain && (
              <div className={`alert alert-${uploadFeedbackLain.type} py-1 px-2 mb-3`} style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-info-circle-fill me-1"></i>
                {uploadFeedbackLain.text}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseLainUploadModal} disabled={isUploadingLain}>
                Batal
              </Button>
              <Button type="submit" size="sm" className="fw-bold text-white" style={{ background: '#475569', borderColor: '#475569', fontSize: '0.78rem' }} disabled={isUploadingLain}>
                {isUploadingLain ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Upload File Lain-lain
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL KONFIRMASI EXPORT EXCEL ══ */}
      <Modal show={showConfirmExportModal} onHide={() => { if (!isExporting) setShowConfirmExportModal(false) }} centered backdrop="static">
        <Modal.Header closeButton={!isExporting} style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-excel-fill fs-5 text-warning"></i>
            <span>Konfirmasi Export Excel</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center bg-light">
          {!isExporting ? (
            <>
              <div className="mb-3">
                <i className="bi bi-file-earmark-spreadsheet fs-1" style={{ color: '#10b981' }}></i>
              </div>
              <h5 className="mb-2 fw-bold text-dark">Download data ke Excel?</h5>
              <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                Anda akan mengunduh data tagihan untuk periode <br/><strong>{formatYmdToDdmmyyyy(periode1)}</strong> s/d <strong>{formatYmdToDdmmyyyy(periode2)}</strong>.<br/>
              </p>
              <div className="d-flex justify-content-center gap-2">
                <Button variant="secondary" onClick={() => setShowConfirmExportModal(false)} className="px-4 fw-semibold" style={{ fontSize: '0.8rem' }}>
                  Tidak, Batal
                </Button>
                <Button variant="success" onClick={handleExportExcel} className="px-4 fw-semibold" style={{ background: '#10b981', borderColor: '#10b981', fontSize: '0.8rem' }}>
                  Ya, Download
                </Button>
              </div>
            </>
          ) : (
            <div className="py-3">
              <h6 className="fw-bold text-dark mb-3">Sedang Mengunduh...</h6>
              <ProgressBar 
                animated={exportProgress < 100} 
                variant="success" 
                now={exportProgress} 
                label={`${exportProgress}%`} 
                style={{ height: '20px', borderRadius: '10px' }} 
              />
              <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.75rem' }}>
                Mohon tunggu, jangan tutup jendela ini.
              </p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ══ MODAL KONFIRMASI BUAT EXCEL TAGIHAN ══ */}
      <Modal show={showConfirmExportTagihanModal} onHide={() => { if (!isExportingTagihan) setShowConfirmExportTagihanModal(false) }} centered backdrop="static">
        <Modal.Header closeButton={!isExportingTagihan} style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-spreadsheet-fill fs-5 text-warning"></i>
            <span>Konfirmasi Buat Excel Tagihan</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center bg-light">
          {!isExportingTagihan ? (
            <>
              <div className="mb-3">
                <i className="bi bi-file-earmark-spreadsheet fs-1" style={{ color: '#0284c7' }}></i>
              </div>
              <h5 className="mb-2 fw-bold text-dark">Download data Excel Tagihan?</h5>
              <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
                Anda akan mengunduh data tagihan untuk periode <br/><strong>{formatYmdToDdmmyyyy(periode1)}</strong> s/d <strong>{formatYmdToDdmmyyyy(periode2)}</strong>.<br/>
              </p>
              <div className="d-flex justify-content-center gap-2">
                <Button variant="secondary" onClick={() => setShowConfirmExportTagihanModal(false)} className="px-4 fw-semibold" style={{ fontSize: '0.8rem' }}>
                  Tidak, Batal
                </Button>
                <Button variant="info" onClick={handleExportTagihanExcel} className="px-4 fw-semibold text-white" style={{ background: '#0284c7', borderColor: '#0284c7', fontSize: '0.8rem' }}>
                  Ya, Download
                </Button>
              </div>
            </>
          ) : (
            <div className="py-3">
              <h6 className="fw-bold text-dark mb-3">Sedang Mengunduh...</h6>
              <ProgressBar 
                animated={exportTagihanProgress < 100} 
                variant="info" 
                now={exportTagihanProgress} 
                label={`${exportTagihanProgress}%`} 
                style={{ height: '20px', borderRadius: '10px' }} 
              />
              <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.75rem' }}>
                Mohon tunggu, jangan tutup jendela ini.
              </p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* ══ MODAL KONFIRMASI GABUNG ALL PDF ══ */}
      <Modal show={showConfirmMergeAllModal} onHide={() => setShowConfirmMergeAllModal(false)} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-question-circle-fill fs-5 text-warning"></i>
            <span>Konfirmasi Gabung Semua PDF</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center bg-light">
          <div className="mb-3">
            <i className="bi bi-files fs-1" style={{ color: '#e11d48' }}></i>
          </div>
          <h5 className="mb-2 fw-bold text-dark">Mau proses gabung semua PDF?</h5>
          <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
            Anda akan memproses gabung dokumen PDF massal untuk periode <br/><strong>{formatYmdToDdmmyyyy(periode1)}</strong> s/d <strong>{formatYmdToDdmmyyyy(periode2)}</strong>.<br/><br/>
            Proses ini mungkin memakan waktu beberapa saat tergantung dari jumlah data.
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button variant="secondary" onClick={() => setShowConfirmMergeAllModal(false)} className="px-4 fw-semibold" style={{ fontSize: '0.8rem' }}>
              Tidak, Batal
            </Button>
            <Button variant="danger" onClick={handleMergeAllPdf} className="px-4 fw-semibold" style={{ background: '#e11d48', borderColor: '#e11d48', fontSize: '0.8rem' }}>
              Ya, Proses Sekarang
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL KONFIRMASI GABUNG PDF ══ */}
      <Modal show={showGabungConfirm} onHide={handleCloseGabungConfirm} centered backdrop="static">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.9rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-question-circle-fill fs-5 text-warning"></i>
            <span>Konfirmasi Proses Gabung PDF</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 text-center bg-light">
          <div className="mb-3">
            <i className="bi bi-file-earmark-zip fs-1 text-dark"></i>
          </div>
          <h5 className="mb-2 fw-bold text-dark">Mau proses gabung PDF?</h5>
          <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
            Anda akan memproses gabung dokumen PDF untuk Pasien <strong>{gabungConfirmItem?.xnmpasien}</strong> dengan No. SEP <strong>{gabungConfirmItem?.no_sep}</strong>.
          </p>
          <div className="d-flex justify-content-center gap-2">
            <Button variant="secondary" onClick={handleCloseGabungConfirm} className="px-4 fw-semibold">
              Tidak, Batal
            </Button>
            <Button variant="dark" onClick={handleConfirmGabung} className="px-4 fw-semibold">
              Ya, Proses Sekarang
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL BATCH UPLOAD ALL SEP FILES (MULTIPLE PDF) ══ */}
      <Modal show={showBatchSepModal} onHide={handleCloseBatchSepModal} centered backdrop="static" size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-folder-symlink-fill fs-5 text-warning"></i>
            <span>Upload All SEP Pasien (Batch / Multiple Files)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleBatchSepUploadSubmit}>
            <div className="alert alert-emerald border-0 shadow-sm p-3 mb-3" style={{ background: '#ecfdf5', borderLeft: '4px solid #10b981', borderRadius: '8px' }}>
              <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-info-circle-fill me-1 text-success"></i> Panduan Upload All SEP Sekaligus
              </div>
              <div className="text-dark" style={{ fontSize: '0.76rem' }}>
                Anda dapat memilih <strong>banyak file PDF SEP sekaligus</strong> (misal: <code>0117R0750826V000550.pdf</code>, <code>0117R0750826V000551.pdf</code>). Nama file PDF akan otomatis dipetakan ke Nomor SEP pasien.
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.8rem' }}>
                Pilih Semua File PDF SEP (Bisa Pilih Banyak File Sekaligus)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                multiple
                onChange={handleBatchSepFilesChange}
                disabled={isBatchUploading}
                style={{ fontSize: '0.82rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                Gunakan <kbd>Ctrl</kbd> + Klik atau <kbd>Shift</kbd> + Klik pada windows explorer untuk memilih beberapa file PDF sekaligus.
              </Form.Text>
            </Form.Group>

            {batchSepFiles.length > 0 && (
              <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-between" style={{ fontSize: '0.76rem' }}>
                <div>
                  <i className="bi bi-files me-1"></i> Total File Terpilih: <strong>{batchSepFiles.length} File PDF</strong>
                </div>
                <Badge bg="success" style={{ fontSize: '0.72rem' }}>Siap Diupload</Badge>
              </div>
            )}

            {isBatchUploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.75rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#059669' }}></i>{batchStatusText}</span>
                  <span className="fw-bold" style={{ color: '#059669' }}>{batchProgress}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="success"
                  now={batchProgress}
                  style={{ height: '16px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {batchLog.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold text-dark mb-1" style={{ fontSize: '0.76rem' }}>Log Hasil Upload Batch:</div>
                <div className="border rounded p-2 bg-white" style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '0.72rem' }}>
                  {batchLog.map((log, idx) => (
                    <div key={idx} className={`d-flex align-items-center justify-content-between py-1 border-bottom ${log.status === 'success' ? 'text-success' : 'text-dark'}`}>
                      <span><strong>{idx + 1}. {log.fileName}</strong> ({log.no_sep})</span>
                      <Badge bg={log.status === 'success' ? 'success' : log.status === 'danger' ? 'danger' : 'warning'} style={{ fontSize: '0.65rem' }}>
                        {log.msg}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseBatchSepModal} disabled={isBatchUploading}>
                {batchProgress === 100 ? 'Selesai' : 'Batal'}
              </Button>
              <Button
                type="submit"
                size="sm"
                className="fw-bold text-white shadow-sm"
                style={{ background: '#059669', borderColor: '#047857', fontSize: '0.78rem' }}
                disabled={isBatchUploading || batchSepFiles.length === 0}
              >
                {isBatchUploading ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Proses Upload All SEP ({batchSepFiles.length})
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      {/* ══ MODAL BATCH UPLOAD ALL TAGIHAN FILES (MULTIPLE PDF) ══ */}
      <Modal show={showBatchTagihanModal} onHide={handleCloseBatchTagihanModal} centered backdrop="static" size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-pdf-fill fs-5 text-warning"></i>
            <span>Upload All Tagihan Pasien (Batch / Multiple Files)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleBatchTagihanUploadSubmit}>
            <div className="alert alert-primary border-0 shadow-sm p-3 mb-3" style={{ background: '#e0f2fe', borderLeft: '4px solid #0284c7', borderRadius: '8px' }}>
              <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-info-circle-fill me-1 text-primary"></i> Panduan Upload All Tagihan Sekaligus
              </div>
              <div className="text-dark" style={{ fontSize: '0.76rem' }}>
                Anda dapat memilih <strong>banyak file PDF Tagihan sekaligus</strong> (misal: <code>0117R0750826V000550.pdf</code>). Nama file PDF akan dipetakan.
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.8rem' }}>
                Pilih Semua File PDF Tagihan (Bisa Pilih Banyak File Sekaligus)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                multiple
                onChange={handleBatchTagihanFilesChange}
                disabled={isBatchUploadingTagihan}
                style={{ fontSize: '0.82rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                Gunakan <kbd>Ctrl</kbd> + Klik atau <kbd>Shift</kbd> + Klik untuk memilih beberapa file PDF sekaligus.
              </Form.Text>
            </Form.Group>

            {batchTagihanFiles.length > 0 && (
              <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-between" style={{ fontSize: '0.76rem' }}>
                <div>
                  <i className="bi bi-files me-1"></i> Total File Terpilih: <strong>{batchTagihanFiles.length} File PDF</strong>
                </div>
                <Badge bg="primary" style={{ fontSize: '0.72rem' }}>Siap Diupload</Badge>
              </div>
            )}

            {isBatchUploadingTagihan && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.75rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#0284c7' }}></i>{batchStatusTextTagihan}</span>
                  <span className="fw-bold" style={{ color: '#0284c7' }}>{batchProgressTagihan}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="primary"
                  now={batchProgressTagihan}
                  style={{ height: '16px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {batchLogTagihan.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold text-dark mb-1" style={{ fontSize: '0.76rem' }}>Log Hasil Upload Batch:</div>
                <div className="border rounded p-2 bg-white" style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '0.72rem' }}>
                  {batchLogTagihan.map((log, idx) => (
                    <div key={idx} className={`d-flex align-items-center justify-content-between py-1 border-bottom ${log.status === 'success' ? 'text-success' : 'text-dark'}`}>
                      <span><strong>{idx + 1}. {log.fileName}</strong> ({log.no_sep})</span>
                      <Badge bg={log.status === 'success' ? 'success' : log.status === 'danger' ? 'danger' : 'warning'} style={{ fontSize: '0.65rem' }}>
                        {log.msg}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseBatchTagihanModal} disabled={isBatchUploadingTagihan}>
                {batchProgressTagihan === 100 ? 'Selesai' : 'Batal'}
              </Button>
              <Button
                type="submit"
                size="sm"
                className="fw-bold text-white shadow-sm"
                style={{ background: '#0284c7', borderColor: '#0369a1', fontSize: '0.78rem' }}
                disabled={isBatchUploadingTagihan || batchTagihanFiles.length === 0}
              >
                {isBatchUploadingTagihan ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Proses Upload All Tagihan ({batchTagihanFiles.length})
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL BATCH UPLOAD ALL INACBGS FILES (MULTIPLE PDF) ══ */}
      <Modal show={showBatchInacbgsModal} onHide={handleCloseBatchInacbgsModal} centered backdrop="static" size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-medical-fill fs-5 text-warning"></i>
            <span>Upload All INA-CBGS Pasien (Batch / Multiple Files)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleBatchInacbgsUploadSubmit}>
            <div className="alert alert-danger border-0 shadow-sm p-3 mb-3" style={{ background: '#ffe4e6', borderLeft: '4px solid #e11d48', borderRadius: '8px' }}>
              <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-info-circle-fill me-1 text-danger"></i> Panduan Upload All INA-CBGS Sekaligus
              </div>
              <div className="text-dark" style={{ fontSize: '0.76rem' }}>
                Anda dapat memilih <strong>banyak file PDF INA-CBGS sekaligus</strong> (misal: <code>0117R0750826V000550.pdf</code>). Nama file PDF akan dipetakan secara otomatis.
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.8rem' }}>
                Pilih Semua File PDF INA-CBGS (Bisa Pilih Banyak File Sekaligus)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                multiple
                onChange={handleBatchInacbgsFilesChange}
                disabled={isBatchUploadingInacbgs}
                style={{ fontSize: '0.82rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                Gunakan <kbd>Ctrl</kbd> + Klik atau <kbd>Shift</kbd> + Klik untuk memilih beberapa file PDF sekaligus.
              </Form.Text>
            </Form.Group>

            {batchInacbgsFiles.length > 0 && (
              <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center justify-content-between" style={{ fontSize: '0.76rem' }}>
                <div>
                  <i className="bi bi-files me-1"></i> Total File Terpilih: <strong>{batchInacbgsFiles.length} File PDF</strong>
                </div>
                <Badge bg="danger" style={{ fontSize: '0.72rem' }}>Siap Diupload</Badge>
              </div>
            )}

            {isBatchUploadingInacbgs && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.75rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#e11d48' }}></i>{batchStatusTextInacbgs}</span>
                  <span className="fw-bold" style={{ color: '#e11d48' }}>{batchProgressInacbgs}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="danger"
                  now={batchProgressInacbgs}
                  style={{ height: '16px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {batchLogInacbgs.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold text-dark mb-1" style={{ fontSize: '0.76rem' }}>Log Hasil Upload Batch:</div>
                <div className="border rounded p-2 bg-white" style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '0.72rem' }}>
                  {batchLogInacbgs.map((log, idx) => (
                    <div key={idx} className={`d-flex align-items-center justify-content-between py-1 border-bottom ${log.status === 'success' ? 'text-success' : 'text-dark'}`}>
                      <span><strong>{idx + 1}. {log.fileName}</strong> ({log.no_sep})</span>
                      <Badge bg={log.status === 'success' ? 'success' : log.status === 'danger' ? 'danger' : 'warning'} style={{ fontSize: '0.65rem' }}>
                        {log.msg}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseBatchInacbgsModal} disabled={isBatchUploadingInacbgs}>
                {batchProgressInacbgs === 100 ? 'Selesai' : 'Batal'}
              </Button>
              <Button
                type="submit"
                size="sm"
                className="fw-bold text-white shadow-sm"
                style={{ background: '#e11d48', borderColor: '#be123c', fontSize: '0.78rem' }}
                disabled={isBatchUploadingInacbgs || batchInacbgsFiles.length === 0}
              >
                {isBatchUploadingInacbgs ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Proses Upload All INA-CBGS ({batchInacbgsFiles.length})
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL BATCH UPLOAD ALL LABORATORIUM FILES (MULTIPLE PDF) ══ */}
      <Modal show={showBatchLabModal} onHide={handleCloseBatchLabModal} centered backdrop="static" size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-bar-graph-fill fs-5 text-warning"></i>
            <span>Upload All Laboratorium Pasien (Batch / Multiple Files)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleBatchLabUploadSubmit}>
            <div className="alert alert-info border-0 shadow-sm p-3 mb-3" style={{ background: '#cffafe', borderLeft: '4px solid #0891b2', borderRadius: '8px' }}>
              <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-info-circle-fill me-1 text-info"></i> Panduan Upload All Laboratorium Sekaligus
              </div>
              <div className="text-dark" style={{ fontSize: '0.76rem' }}>
                Anda dapat memilih <strong>banyak file PDF Laboratorium sekaligus</strong> (misal: <code>0117R0750826V000550.pdf</code>). Nama file PDF akan dipetakan secara otomatis.
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.8rem' }}>
                Pilih Semua File PDF Laboratorium (Bisa Pilih Banyak File Sekaligus)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                multiple
                onChange={handleBatchLabFilesChange}
                disabled={isBatchUploadingLab}
                style={{ fontSize: '0.82rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                Gunakan <kbd>Ctrl</kbd> + Klik atau <kbd>Shift</kbd> + Klik untuk memilih beberapa file PDF sekaligus.
              </Form.Text>
            </Form.Group>

            {batchLabFiles.length > 0 && (
              <div className="alert alert-primary py-2 px-3 mb-3 d-flex align-items-center justify-content-between" style={{ fontSize: '0.76rem' }}>
                <div>
                  <i className="bi bi-files me-1"></i> Total File Terpilih: <strong>{batchLabFiles.length} File PDF</strong>
                </div>
                <Badge bg="info" style={{ fontSize: '0.72rem' }}>Siap Diupload</Badge>
              </div>
            )}

            {isBatchUploadingLab && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.75rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#0891b2' }}></i>{batchStatusTextLab}</span>
                  <span className="fw-bold" style={{ color: '#0891b2' }}>{batchProgressLab}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="info"
                  now={batchProgressLab}
                  style={{ height: '16px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {batchLogLab.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold text-dark mb-1" style={{ fontSize: '0.76rem' }}>Log Hasil Upload Batch:</div>
                <div className="border rounded p-2 bg-white" style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '0.72rem' }}>
                  {batchLogLab.map((log, idx) => (
                    <div key={idx} className={`d-flex align-items-center justify-content-between py-1 border-bottom ${log.status === 'success' ? 'text-success' : 'text-dark'}`}>
                      <span><strong>{idx + 1}. {log.fileName}</strong> ({log.no_sep})</span>
                      <Badge bg={log.status === 'success' ? 'success' : log.status === 'danger' ? 'danger' : 'warning'} style={{ fontSize: '0.65rem' }}>
                        {log.msg}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseBatchLabModal} disabled={isBatchUploadingLab}>
                {batchProgressLab === 100 ? 'Selesai' : 'Batal'}
              </Button>
              <Button
                type="submit"
                size="sm"
                className="fw-bold text-white shadow-sm"
                style={{ background: '#0891b2', borderColor: '#0e7490', fontSize: '0.78rem' }}
                disabled={isBatchUploadingLab || batchLabFiles.length === 0}
              >
                {isBatchUploadingLab ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Proses Upload All Lab ({batchLabFiles.length})
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL BATCH UPLOAD ALL RADIOLOGI FILES (MULTIPLE PDF) ══ */}
      <Modal show={showBatchRadModal} onHide={handleCloseBatchRadModal} centered backdrop="static" size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-image-fill fs-5 text-warning"></i>
            <span>Upload All Radiologi Pasien (Batch / Multiple Files)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleBatchRadUploadSubmit}>
            <div className="alert alert-secondary border-0 shadow-sm p-3 mb-3" style={{ background: '#f1f5f9', borderLeft: '4px solid #64748b', borderRadius: '8px' }}>
              <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-info-circle-fill me-1 text-secondary"></i> Panduan Upload All Radiologi Sekaligus
              </div>
              <div className="text-dark" style={{ fontSize: '0.76rem' }}>
                Anda dapat memilih <strong>banyak file PDF Radiologi sekaligus</strong> (misal: <code>0117R0750826V000550.pdf</code>). Nama file PDF akan dipetakan secara otomatis.
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.8rem' }}>
                Pilih Semua File PDF Radiologi (Bisa Pilih Banyak File Sekaligus)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                multiple
                onChange={handleBatchRadFilesChange}
                disabled={isBatchUploadingRad}
                style={{ fontSize: '0.82rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                Gunakan <kbd>Ctrl</kbd> + Klik atau <kbd>Shift</kbd> + Klik untuk memilih beberapa file PDF sekaligus.
              </Form.Text>
            </Form.Group>

            {batchRadFiles.length > 0 && (
              <div className="alert alert-secondary py-2 px-3 mb-3 d-flex align-items-center justify-content-between" style={{ fontSize: '0.76rem' }}>
                <div>
                  <i className="bi bi-files me-1"></i> Total File Terpilih: <strong>{batchRadFiles.length} File PDF</strong>
                </div>
                <Badge bg="secondary" style={{ fontSize: '0.72rem' }}>Siap Diupload</Badge>
              </div>
            )}

            {isBatchUploadingRad && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.75rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#64748b' }}></i>{batchStatusTextRad}</span>
                  <span className="fw-bold" style={{ color: '#64748b' }}>{batchProgressRad}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="secondary"
                  now={batchProgressRad}
                  style={{ height: '16px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {batchLogRad.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold text-dark mb-1" style={{ fontSize: '0.76rem' }}>Log Hasil Upload Batch:</div>
                <div className="border rounded p-2 bg-white" style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '0.72rem' }}>
                  {batchLogRad.map((log, idx) => (
                    <div key={idx} className={`d-flex align-items-center justify-content-between py-1 border-bottom ${log.status === 'success' ? 'text-success' : 'text-dark'}`}>
                      <span><strong>{idx + 1}. {log.fileName}</strong> ({log.no_sep})</span>
                      <Badge bg={log.status === 'success' ? 'success' : log.status === 'danger' ? 'danger' : 'warning'} style={{ fontSize: '0.65rem' }}>
                        {log.msg}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseBatchRadModal} disabled={isBatchUploadingRad}>
                {batchProgressRad === 100 ? 'Selesai' : 'Batal'}
              </Button>
              <Button
                type="submit"
                size="sm"
                className="fw-bold text-white shadow-sm"
                style={{ background: '#64748b', borderColor: '#475569', fontSize: '0.78rem' }}
                disabled={isBatchUploadingRad || batchRadFiles.length === 0}
              >
                {isBatchUploadingRad ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Proses Upload All Rad ({batchRadFiles.length})
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ══ MODAL BATCH UPLOAD ALL LAIN-LAIN FILES (MULTIPLE PDF) ══ */}
      <Modal show={showBatchLainModal} onHide={handleCloseBatchLainModal} centered backdrop="static" size="lg">
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)', color: '#fff' }}>
          <Modal.Title style={{ fontSize: '0.92rem', fontWeight: '700' }} className="d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-medical fs-5 text-warning"></i>
            <span>Upload All Lain-lain Pasien (Batch / Multiple Files)</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          <Form onSubmit={handleBatchLainUploadSubmit}>
            <div className="alert alert-dark border-0 shadow-sm p-3 mb-3" style={{ background: '#f8fafc', borderLeft: '4px solid #334155', borderRadius: '8px' }}>
              <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-info-circle-fill me-1 text-dark"></i> Panduan Upload All Lain-lain Sekaligus
              </div>
              <div className="text-dark" style={{ fontSize: '0.76rem' }}>
                Anda dapat memilih <strong>banyak file PDF Lain-lain sekaligus</strong> (misal: <code>0117R0750826V000550.pdf</code>). Nama file PDF akan dipetakan secara otomatis.
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '0.8rem' }}>
                Pilih Semua File PDF Lain-lain (Bisa Pilih Banyak File Sekaligus)
              </Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf, .pdf"
                multiple
                onChange={handleBatchLainFilesChange}
                disabled={isBatchUploadingLain}
                style={{ fontSize: '0.82rem' }}
              />
              <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                Gunakan <kbd>Ctrl</kbd> + Klik atau <kbd>Shift</kbd> + Klik untuk memilih beberapa file PDF sekaligus.
              </Form.Text>
            </Form.Group>

            {batchLainFiles.length > 0 && (
              <div className="alert alert-dark py-2 px-3 mb-3 d-flex align-items-center justify-content-between" style={{ fontSize: '0.76rem' }}>
                <div>
                  <i className="bi bi-files me-1"></i> Total File Terpilih: <strong>{batchLainFiles.length} File PDF</strong>
                </div>
                <Badge bg="dark" style={{ fontSize: '0.72rem' }}>Siap Diupload</Badge>
              </div>
            )}

            {isBatchUploadingLain && (
              <div className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold text-dark mb-1" style={{ fontSize: '0.75rem' }}>
                  <span><i className="bi bi-cloud-arrow-up-fill me-1" style={{ color: '#334155' }}></i>{batchStatusTextLain}</span>
                  <span className="fw-bold" style={{ color: '#334155' }}>{batchProgressLain}%</span>
                </div>
                <ProgressBar
                  animated
                  striped
                  variant="dark"
                  now={batchProgressLain}
                  style={{ height: '16px', borderRadius: '8px', backgroundColor: '#e2e8f0' }}
                />
              </div>
            )}

            {batchLogLain.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold text-dark mb-1" style={{ fontSize: '0.76rem' }}>Log Hasil Upload Batch:</div>
                <div className="border rounded p-2 bg-white" style={{ maxHeight: '180px', overflowY: 'auto', fontSize: '0.72rem' }}>
                  {batchLogLain.map((log, idx) => (
                    <div key={idx} className={`d-flex align-items-center justify-content-between py-1 border-bottom ${log.status === 'success' ? 'text-success' : 'text-dark'}`}>
                      <span><strong>{idx + 1}. {log.fileName}</strong> ({log.no_sep})</span>
                      <Badge bg={log.status === 'success' ? 'success' : log.status === 'danger' ? 'danger' : 'warning'} style={{ fontSize: '0.65rem' }}>
                        {log.msg}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 border-top pt-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleCloseBatchLainModal} disabled={isBatchUploadingLain}>
                {batchProgressLain === 100 ? 'Selesai' : 'Batal'}
              </Button>
              <Button
                type="submit"
                size="sm"
                className="fw-bold text-white shadow-sm"
                style={{ background: '#334155', borderColor: '#1e293b', fontSize: '0.78rem' }}
                disabled={isBatchUploadingLain || batchLainFiles.length === 0}
              >
                {isBatchUploadingLain ? <Spinner size="sm" animation="border" className="me-1" /> : <i className="bi bi-cloud-upload-fill me-1"></i>}
                Proses Upload All Lain-lain ({batchLainFiles.length})
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

    </div>
  );
};

// ══════════════ STYLES ══════════════
const styles = {
  appContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 40%, #e2e8f0 100%)',
    display: 'flex', flexDirection: 'column',
  },
  navbarCustom: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0d9488 100%)',
    padding: '0.25rem 1rem', flexShrink: 0,
  },
  mainContent: {
    flex: 1, padding: '0.3rem', overflowY: 'auto',
  },
  mainCard: {
    borderRadius: '12px', border: '1.5px solid #cbd5e1',
    boxShadow: '0 8px 30px rgba(15, 23, 42, 0.06)',
    background: '#ffffff', overflow: 'hidden',
  },
  cardHeader: {
    padding: '0.4rem 0.75rem', color: '#ffffff', fontWeight: 600, fontSize: '0.83rem',
  },
  bgMenuHeader: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0d9488 100%)',
  },
};

export default DataTagihanPDF;
